/**
 * O modelo 3D da campanha CPTu (onda 316, Tarefa 316.3; spec 07 §5.7 e §13) — o terreno com a imagem do
 * satélite, as colunas das verticais (a zona MEDIDA), as fatias N–S, E–O e em planta, a cerca das linhas de
 * corte (a MESMA `RespostaDaSecao` da seção 2D) e os voxels das zonas escolhidas.
 *
 * O three.js entra SÓ aqui e SÓ por `import('three')`, dinâmico (R7; a exceção do `300.91`): o pedaço de
 * ≈ 0,7 MB baixa quando a vista abre, nunca antes. Nada de `three/addons`: o giro é nosso, pelo ponteiro e
 * pelo teclado (← → gira, ↑ ↓ inclina, + − aproxima, Home volta), com os mesmos botões na barra de cima — fora
 * do canto de baixo à direita, que o «Relatar» da casa cobre. Fatias, cerca e colunas com materiais SEM LUZ
 * (a cor é a da zona, lida dos tokens `--ts-sbtn-N` e `--ts-escala-N`); o canvas é transparente sobre
 * `--ts-papel`; tudo se descarta ao desmontar (geometrias, materiais, texturas, o contexto WebGL).
 *
 * Os buffers saem do `geometria3d.ts` (puro); a grade, do `calcularGrade` (o worker); a imagem do terreno vem
 * por propriedade (`mapaGeotecnico/imagemDoTerreno.ts` na tela) — o visual não toca na rede. A legenda fixa
 * («modelo interpretativo — entre as verticais o valor é estimado») e o crédito da imagem vão em TEXTO.
 *
 * A cor: a zona só no SBTn; o Su e as contínuas pelas 7 classes `--ts-escala-1…7` (os limites calculados uma vez
 * sobre os visíveis da grade e passados às fatias, à cerca e aos voxels), como na seção 2D. A legenda de cores
 * (as zonas presentes, ou as 7 classes com os limites e a unidade) vai na tela e na captura (P-UI-15).
 *
 * A captura (316.5) compõe SEMPRE no papel do tema CLARO, qualquer que seja o tema da tela: o fundo e a tinta
 * saem da regra `:root` das folhas de estilo (a do tema claro em `design/testesolo.tokens.css`; o escuro mora em
 * `:root[data-tema="escuro"]` e na media query) — um elemento com `data-tema="claro"` não serviria, porque os
 * seletores do tema são do `:root` e um filho herda os valores escuros. O norte da captura gira com o azimute,
 * como o da tela.
 *
 * Os gráficos das verticais (o pedido do Humberto de 16/10/2026, Tarefa 316.3 passos 6–10): com `sobreposicoes`
 * (as MESMAS faixas da seção 2D, `sobreposicoesDasVerticais`, e o mesmo painel), cada vertical ligada ganha uma fita
 * em pé no lugar dela, a partir da face da coluna no lado escolhido (leste de partida; onda 347 §4.7) [esp.fita-3d], com a
 * largura vezes o «Exagero horizontal dos gráficos» (§4.4, só as fitas): na série (qc, qt, fs, u₂…), o valor medido pela
 * largura, com o traço do perfil; na zona SBT/SBTn, a fita inteira na cor da zona (os tokens `--ts-sbtn-N`, as cores
 * do CPeT-IT). A opacidade do painel vale para as fitas (a dos voxels e a do satélite são outras); ligar e desligar
 * refaz a camada e devolve à GPU as geometrias e os materiais da anterior.
 *
 * Sem WebGL (ou com o three.js recusado), a informação não some: a fatia em planta vai em 2D, pela `SecaoEspacial` no
 * modo planta (`fatiaEmPlanta`), com o controle da cota.
 *
 * As colunas (onda 347, spec 2026-10-22 §4.1): cilindros (ou prismas) com o raio dos ajustes, pintados pela zona SBTn
 * medida (de partida), pela zona SBT medida, pela classe da grandeza do modelo ou pela camada geotécnica da fonte escolhida (a
 * cor da unidade); a «Camada geotécnica» fica bloqueada, com o motivo, sem fonte de camadas escolhida. Os ajustes vêm da tela (`ajustes`) e voltam a
 * ela (`aoAjustes`), que os guarda; o clique escolhe a coluna pelo cilindro e lê a cota onde o raio da câmera entra nele.
 *
 * A malha de cota e coordenadas (onda 347, §4.2): linhas finas no piso e nas duas paredes de fundo (as de normal mais alinhada à
 * direção da câmera, que trocam no giro), na tinta `--ts-linha`, com os rótulos de E, N e cota (no sistema das verticais, a
 * regra da 338) na `--ts-tinta-2`. Liga e desliga pelos ajustes (`malha`); desligada, a geometria volta à GPU e os rótulos saem.
 *
 * A superfície do nível d'água (onda 347, §4.3): a cota do NA (boca − NA da premissa de cada vertical, `superficieDoNa`) entre
 * as verticais pelo método do modelo, só dentro do fecho, na tinta `--ts-agua` com a «Opacidade da água» dos ajustes, a borda
 * marcada e um anel no NA de cada vertical; com menos de três verticais com NA, a barra diz por quê.
 *
 * A opacidade das verticais (onda 347, §4.5): só o material das colunas; abaixo de 100 %, transparente e sem escrever a
 * profundidade; a 100 %, opaco, como antes.
 *
 * As camadas geotécnicas (onda 347, §4.6): «Camadas usadas» — as gravadas pelo engenheiro ou a sugestão do T-ratio (`camadas`,
 * da tela; a sugestão sempre com a faixa «Sugestão — não gravada», RN-269) —, cada nó da grade com a unidade de maior indicador
 * interpolado (`unidadesNaGrade`), desenhadas em «Blocos» (uma `InstancedMesh` por unidade) ou «Só contatos» (as faces entre
 * unidades, na cor da de baixo), com a opacidade das camadas; a lista de unidades marca e esconde cada uma, na cor da zona SBTn
 * predominante dela.
 *
 * Para os testes de tela, a caixa expõe `ponteDo3d` (pronto, webgl, colunas, fatias, voxels, satelite, exagero,
 * escalaVertical, camera, projetar, sobreposicoes, fitas, terreno, memoria, malha, agua, camadas). Funciona por propriedade, igual no LabManager e no
 * Portal (spec §3.2).
 */
import type { JSX } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as EventoDeTecla, PointerEvent as EventoDePonteiro } from 'react';
import { ZONAS_SBTN, planoLocal } from '../motor';
import type { PlanoLocal } from '../motor';
import {
  EXAGERO_HORIZONTAL, EXAGERO_VERTICAL, LEGENDA_DO_ESPACIAL, OPACIDADE_DO_SATELITE, OPACIDADE_MINIMA_DAS_VERTICAIS, RAIO_DAS_VERTICAIS, VOXELS_NO_MAXIMO, classeDoValor, colunaPrincipal, fatiaEmPlanta,
  grandezaDaSecao, limitesDoTerreno, linhasDaMalha, normalizarUnidade, paredesDoFundo, superficieDoNa, unidadesNaGrade, zonasDasUnidades,
} from './espacialDaCampanha';
import type {
  AjustesDo3d, CamadaNoModelo, GrandezaDaSecao, LimitesDaImagem, LinhaDeCorte, MalhaDoModelo, ParedeDaMalha, RespostaDaGrade, RespostaDaSecao, SistemaDaMalha,
  SobreposicaoDaVertical, SobreposicoesDasVerticais, SuperficieDoNa, VerticalNoPedido,
} from './espacialDaCampanha';
import {
  cercaDoModelo, classesDaGrade, colunasDoModelo, corDaUnidade, corDoToken, facesDeContato, fatiaDoModelo, fitasDasVerticais, larguraDaFita,
  legendaDoModelo, pintaPelaZona, terrenoDoModelo, voxelsDeUnidade, voxelsDoModelo,
} from './geometria3d';
import type { CoresDoModelo, FitaDaVertical, ItemDaLegenda } from './geometria3d';
import { LegendaSbt } from './LegendaSbt';
import { SecaoEspacial } from './SecaoEspacial';
import { indiceNaProfundidade } from './leitura';
import { numeroBr } from './escalas';

type Tres = typeof import('three');
type Objeto3d = import('three').Object3D;

/** A imagem do terreno, montada pela tela. */
export interface ImagemDoModelo { imagem: CanvasImageSource; limites: LimitesDaImagem; credito: string }

/** O que o modelo entrega a quem o monta: a captura do quadro (316.5). */
export interface ControleDoModelo3d { capturar(o: { largura: number; altura: number }): Promise<Blob> }

/** O estado do desenho, como a tela o ouve (`aoEstado`). */
export type EstadoDoModelo3d = 'carregando' | 'pronto' | 'sem-webgl' | 'erro';

/** Os gráficos das verticais no 3D: as faixas da seção (`sobreposicoesDasVerticais`), as ligadas e a opacidade. */
export interface SobreposicoesDoModelo { dados: SobreposicoesDasVerticais; ligadas: ReadonlySet<string>; opacidade: number }

/** Uma fita como a ponte de teste a lê. */
export interface FitaDaPonte {
  codigo: string; grandeza: string; tipo: 'serie' | 'zona'; opacidade: number; largura: number;
  /**
   * O lado da fita (onda 347 §4.7), o lugar da coluna, o começo da fita (u = 0, na face da coluna) e a ponta (u = a largura
   * toda), no plano (x leste, y norte), m.
   */
  lado: AjustesDo3d['ladoDaFita']; coluna: { x: number; y: number }; base: { x: number; y: number }; ponta: { x: number; y: number };
  /** O que a fita mostra na cota (a leitura mais perto): o valor medido, ou a zona. */
  naCota(cota: number): number;
  /**
   * A largura desenhada da fita na leitura mais perto da cota, em metros (onda 347): a fração da escala × a largura
   * toda; NaN sem a fita, sem a leitura ou na zona.
   */
  larguraNaCota(cota: number): number;
  /** A cor desenhada na cota (lida da geometria, de volta ao sRGB 0–255); null fora da fita. */
  corNaCota(cota: number): [number, number, number] | null;
}

/** As fontes de camada que a tela tem para o 3D (`'nenhuma'` sempre; as gravadas e a sugestão quando existem). */
export type FonteDasCamadas = AjustesDo3d['camadas'];

/**
 * As camadas que a tela entrega ao 3D (§4.6), por código da vertical: as gravadas pelo engenheiro (as vigentes) e a sugestão do
 * T-ratio (calculada no aparelho, com a unidade pela zona SBTn predominante; nunca gravada — RN-269). O Portal só tem as gravadas.
 */
export interface CamadasDoModelo {
  gravadas?: ReadonlyMap<string, readonly CamadaNoModelo[]> | null;
  tratio?: ReadonlyMap<string, readonly CamadaNoModelo[]> | null;
}

/** A faixa da sugestão (RN-269): a sugestão fica sempre marcada. */
export const FAIXA_DA_SUGESTAO = 'Sugestão — não gravada';

/** As escolhas de «Camadas usadas» (§4.6). */
const FONTES_DAS_CAMADAS: readonly { chave: FonteDasCamadas; rotulo: string }[] = [
  { chave: 'nenhuma', rotulo: 'Nenhuma' }, { chave: 'gravadas', rotulo: 'As gravadas pelo engenheiro' }, { chave: 'tratio', rotulo: 'Sugestão · T-ratio' },
];
/** A ordem de desenho das camadas: depois do terreno (2), da água (3) e das colunas transparentes (3,5), com as fitas (4). */
const ORDEM_DAS_CAMADAS = 4;

/** As escolhas do «Colorir as verticais por» (spec §4.1). */
const CORES_DAS_VERTICAIS: readonly { chave: AjustesDo3d['corDasVerticais']; rotulo: string }[] = [
  { chave: 'sbtn', rotulo: 'Zona SBTn' }, { chave: 'sbt', rotulo: 'Zona SBT' },
  { chave: 'camada', rotulo: 'Camada geotécnica' }, { chave: 'grandeza', rotulo: 'Grandeza do modelo' },
];

/**
 * A opacidade das colunas no material (§4.5): abaixo de 100 %, transparente e sem escrever a profundidade (as de trás
 * aparecem), desenhada depois do terreno (2) e da água (3) e antes das camadas e das fitas (4) — senão a água e o
 * satélite pintariam por cima da coluna da frente; a 100 %, opaco, como antes (ordem 0).
 */
const ORDEM_DAS_COLUNAS_TRANSPARENTES = 3.5;
const opacidadeNaColuna = (c: { material: import('three').MeshBasicMaterial; renderOrder: number }, opacidade: number): void => {
  const m = c.material;
  const transparente = opacidade < 1;
  if (m.transparent !== transparente) m.needsUpdate = true;
  m.transparent = transparente;
  m.opacity = opacidade;
  m.depthWrite = !transparente;
  c.renderOrder = transparente ? ORDEM_DAS_COLUNAS_TRANSPARENTES : 0;
};

/** Os lados do cilindro das colunas (o círculo de 16 lados). */
const LADOS_DO_CILINDRO = 16;

/** O lado da fita (spec §4.7): a direção em planta (x leste, y norte) e o rótulo da barra e da legenda. */
const DIRECAO_DO_LADO: Readonly<Record<AjustesDo3d['ladoDaFita'], readonly [number, number]>> = {
  leste: [1, 0], oeste: [-1, 0], norte: [0, 1], sul: [0, -1],
};
const NO_LADO: Readonly<Record<AjustesDo3d['ladoDaFita'], string>> = { leste: 'a leste', oeste: 'a oeste', norte: 'ao norte', sul: 'ao sul' };
const LADOS_DA_FITA: readonly { chave: AjustesDo3d['ladoDaFita']; rotulo: string }[] = [
  { chave: 'leste', rotulo: 'Leste' }, { chave: 'oeste', rotulo: 'Oeste' }, { chave: 'norte', rotulo: 'Norte' }, { chave: 'sul', rotulo: 'Sul' },
];

/** Uma fatia: ligada e a posição (m para leste, m para norte ou a cota). */
interface Fatia { ligada: boolean; posicao: number }
type TipoDeFatia = 'ns' | 'eo' | 'planta';

/** A vista: o azimute (graus, 0 = olhando para o norte), a inclinação (graus acima do horizonte) e a distância (m). */
interface Vista { azimute: number; inclinacao: number; distancia: number }

/** O passo do giro e do zoom — ESCOLHA DA TELA, SEM FONTE [esp.giro-passos] (no `espacialDaCampanha.ts`). */
const GIRO = 15;
const ZOOM = 1.25;
const VISTA_DE_PARTIDA = { azimute: 30, inclinacao: 35 };

const ROTULO_DA_FATIA: Record<TipoDeFatia, string> = { ns: 'Fatia N–S', eo: 'Fatia E–O', planta: 'Fatia em planta' };

/** A zona e o nome, escritos (P-UI-15: a cor nunca vai sozinha). */
const ZONAS = ZONAS_SBTN as unknown as Readonly<Record<number, { nome: string; token: string }>>;
const textoDaZona = (z: number): string => (ZONAS[z] ? `SBTn ${z} · ${ZONAS[z].nome}` : 'sem zona');

/** A porcentagem inteira, «70 %». */
const porcento = (x: number): string => `${numeroBr(Math.round(x * 100), 0)} %`;

type Rgb = [number, number, number];

/**
 * O recurso quando um token de cor falta (os `--ts-escala-1…7` nascem na 316.2): uma escada de tokens que a casa
 * já tem, da tinta ao fundo — nunca uma cor literal. O último recurso é a cor do texto da caixa (o `color`
 * computado sempre vem em `rgb(…)`).
 */
const ESCADA_DE_RECURSO = ['--ts-tinta', '--ts-tinta-2', '--ts-tinta-3', '--ts-linha-forte', '--ts-linha', '--ts-creme', '--ts-fundo'] as const;
function corDeRecurso(estilo: CSSStyleDeclaration, i: number, n: number): Rgb {
  const degrau = ESCADA_DE_RECURSO[Math.round((i * (ESCADA_DE_RECURSO.length - 1)) / Math.max(1, n - 1))];
  return corDoToken(estilo.getPropertyValue(degrau)) ?? corDoToken(estilo.color) ?? (corDoToken(estilo.getPropertyValue('--ts-tinta')) as Rgb);
}

/** As cores dos tokens, lidas da caixa (o tema já aplicado); sem o token, a escada de recurso (o desenho não some). */
function lerCores(el: Element): CoresDoModelo {
  const estilo = getComputedStyle(el);
  const zonas: Record<number, Rgb> = {};
  for (let z = 1; z <= 9; z++) zonas[z] = corDoToken(estilo.getPropertyValue(`--ts-sbtn-${z}`)) ?? corDeRecurso(estilo, z - 1, 9);
  const escala = Array.from({ length: 7 }, (_, i) => corDoToken(estilo.getPropertyValue(`--ts-escala-${i + 1}`)) ?? corDeRecurso(estilo, i, 7));
  return { zonas, escala };
}

/** Um cinza de token para o que não tem zona (o terreno sem imagem, a leitura sem zona): o token ou o recurso. */
const cinzaDoToken = (el: Element, token: string): Rgb => corDoToken(getComputedStyle(el).getPropertyValue(token)) ?? corDeRecurso(getComputedStyle(el), 2, 7);

/** A cor RGB para o SVG da legenda e o canvas da captura (a cor lida do token, nunca escrita à mão). */
const rgb = (c: Rgb | undefined): string => (c ? `rgb(${c[0]}, ${c[1]}, ${c[2]})` : 'transparent');

/**
 * As cores do papel CLARO para a captura: lidas da regra `:root` das folhas de estilo (a do tema claro; o escuro
 * vive em `:root[data-tema="escuro"]`, `:root[data-theme="dark"]` e na media query, que este seletor exato não
 * pega). Sem a regra (folha de outra origem, ambiente sem CSSOM), o valor computado da caixa.
 */
function coresDoPapelClaro(el: Element): { papel: string; tinta: string; tinta3: string } {
  const nomes = ['--ts-papel', '--ts-tinta', '--ts-tinta-3'] as const;
  const lidas: Partial<Record<(typeof nomes)[number], string>> = {};
  const visitar = (regras: CSSRuleList): void => {
    for (const r of Array.from(regras)) {
      if (typeof CSSImportRule !== 'undefined' && r instanceof CSSImportRule) {
        try { if (r.styleSheet) visitar(r.styleSheet.cssRules); } catch { /* folha de outra origem */ }
        continue;
      }
      if (!(r instanceof CSSStyleRule) || r.selectorText.trim() !== ':root') continue;
      for (const n of nomes) {
        const v = r.style.getPropertyValue(n).trim();
        if (v && !v.startsWith('var(') && !lidas[n]) lidas[n] = v;
      }
    }
  };
  for (const folha of Array.from(document.styleSheets)) {
    try { visitar(folha.cssRules); } catch { /* folha de outra origem: não se lê */ }
  }
  return {
    papel: lidas['--ts-papel'] ?? corCss(el, '--ts-papel'),
    tinta: lidas['--ts-tinta'] ?? corCss(el, '--ts-tinta'),
    tinta3: lidas['--ts-tinta-3'] ?? corCss(el, '--ts-tinta-3'),
  };
}

/** Uma cor de token para o desenho 2D (a captura): o valor do token, ou o da cor do texto da caixa. */
const corCss = (el: Element, token: string): string => getComputedStyle(el).getPropertyValue(token).trim() || getComputedStyle(el).color;

export function Modelo3d(p: {
  verticais: readonly VerticalNoPedido[]; grade: RespostaDaGrade | null; grandeza: GrandezaDaSecao;
  linhas: readonly LinhaDeCorte[]; secoes?: Readonly<Record<string, RespostaDaSecao>>;
  imagem: ImagemDoModelo | null; motivoSemImagem?: string | null; aoRecarregarImagem?(): void;
  mostrarNaoHomologadas?: boolean; celular?: boolean; aoPronto?(m: ControleDoModelo3d): void;
  /** Os gráficos das verticais (o pedido de 16/10): as fitas, com o mesmo painel da seção. */
  sobreposicoes?: SobreposicoesDoModelo | null;
  /** O estado do desenho (a tela sabe quando não há WebGL). */
  aoEstado?(e: EstadoDoModelo3d): void;
  /** Os ajustes da barra (onda 347, §4): a tela guarda; o modelo só desenha e devolve o que mudou por `aoAjustes`. */
  ajustes: AjustesDo3d;
  aoAjustes(a: AjustesDo3d): void;
  /** As fontes de camada que existem nesta campanha: sem `'gravadas'` nem `'tratio'`, «Camada geotécnica» fica bloqueada. */
  camadasDisponiveis: readonly FonteDasCamadas[];
  /** O sistema em que as verticais foram lançadas (`sistemaDaMalha`, a regra da 338), para os rótulos da malha; sem ele, graus. */
  sistema?: SistemaDaMalha;
  /** As camadas de cada fonte (§4.6); sem elas, nenhuma camada se desenha. */
  camadas?: CamadasDoModelo | null;
}): JSX.Element {
  const caixa = useRef<HTMLElement>(null);
  const tela = useRef<HTMLDivElement>(null);
  const rotulos = useRef<HTMLDivElement>(null);
  const rotulosDaMalha = useRef<HTMLDivElement>(null);
  const norte = useRef<HTMLSpanElement>(null);
  const [estado, setEstado] = useState<EstadoDoModelo3d>('carregando');
  const [erro, setErro] = useState<string | null>(null);
  const [exagero, setExagero] = useState(EXAGERO_VERTICAL);
  const [opacidade, setOpacidade] = useState(OPACIDADE_DO_SATELITE);
  const [comSatelite, setComSatelite] = useState(true);
  const [comCerca, setComCerca] = useState(true);
  const [zonasDosVoxels, setZonasDosVoxels] = useState<ReadonlySet<number>>(new Set());
  const [fatias, setFatias] = useState<Record<TipoDeFatia, Fatia>>({
    ns: { ligada: true, posicao: 0 }, eo: { ligada: false, posicao: 0 }, planta: { ligada: false, posicao: 0 },
  });
  const [vista, setVista] = useState<Vista>({ ...VISTA_DE_PARTIDA, distancia: 0 });
  const [leitura, setLeitura] = useState<string | null>(null);
  const [satelitePronto, setSatelitePronto] = useState(false);

  const [versaoDaCena, setVersaoDaCena] = useState(0);
  const [cores, setCores] = useState<CoresDoModelo | null>(null);

  const g = p.grade;
  const insuficiente = g?.insuficiente ?? null;
  const principal = colunaPrincipal(p.grandeza);
  const def = grandezaDaSecao(p.grandeza);
  const aj = p.ajustes;
  const mudar = (x: Partial<AjustesDo3d>): void => p.aoAjustes({ ...aj, ...x });
  const opacidadeDasVerticaisRef = useRef(aj.opacidadeDasVerticais);
  opacidadeDasVerticaisRef.current = aj.opacidadeDasVerticais;
  // A fonte das camadas que vale (§4.6): a escolhida, se a tela a tem com dados; senão, nenhuma.
  const mapaDe = (f: FonteDasCamadas): ReadonlyMap<string, readonly CamadaNoModelo[]> | null =>
    (f === 'gravadas' ? p.camadas?.gravadas ?? null : f === 'tratio' ? p.camadas?.tratio ?? null : null);
  // A fonte que a tela tem (a sugestão só se calcula quando escolhida: até chegar, nada se desenha).
  const fonteServe = (f: FonteDasCamadas): boolean => f === 'nenhuma' || p.camadasDisponiveis.includes(f);
  const fonte: FonteDasCamadas = fonteServe(aj.camadas) ? aj.camadas : 'nenhuma';
  const mapaDasCamadas = mapaDe(fonte);
  const sugestao = fonte === 'tratio';
  // O que bloqueia cada cor das verticais (null = livre): a camada sem fonte escolhida; a grandeza no SBTn (a zona já é a cor).
  const temGravadas = fonteServe('gravadas'), temSugestao = fonteServe('tratio');
  const motivoDaCamada = fonte !== 'nenhuma' ? null
    : !temGravadas ? (temSugestao ? 'nenhuma vertical tem camada gravada: escolha «Sugestão · T-ratio» em «Camadas usadas»' : 'nenhuma vertical tem camada gravada')
      : 'escolha a fonte em «Camadas usadas»';
  const bloqueioDaCor = (c: AjustesDo3d['corDasVerticais']): string | null => (c === 'camada' ? motivoDaCamada
    : c === 'grandeza' && pintaPelaZona(p.grandeza) ? 'o modelo está no SBTn: escolha Ic, qc, qt, fs, u₂ ou Su para colorir pela grandeza' : null);
  // A cor que vale: a escolhida, ou a zona SBTn quando a escolhida está bloqueada.
  const corDasColunas: AjustesDo3d['corDasVerticais'] = bloqueioDaCor(aj.corDasVerticais) ? 'sbtn' : aj.corDasVerticais;

  // O plano local VEM DA GRADE: o centroide das verticais que ela usou (as do pedido com coordenada, cota e
  // leituras, pelo código) — a mesma conta do `calcularGrade`; sem grade, o mesmo filtro dele.
  const plano: PlanoLocal = useMemo(() => {
    const daGrade = g ? new Set(g.verticais.map((v) => v.codigo)) : null;
    const uteis = p.verticais.filter((v) => (daGrade ? daGrade.has(v.codigo)
      : Number.isFinite(v.latitude) && Number.isFinite(v.longitude) && Number.isFinite(v.cota) && v.z.length > 0));
    return planoLocal(uteis);
  }, [g, p.verticais]);
  const limites: LimitesDaImagem = useMemo(() => p.imagem?.limites ?? limitesDoTerreno(p.verticais), [p.imagem, p.verticais]);
  // As classes da escala (Su e contínuas): UMA vez sobre os visíveis da grade, para fatias, cerca, voxels e legenda.
  const classes = useMemo(() => (g && !insuficiente ? classesDaGrade(g) : { limites: [] as number[], dominio: [NaN, NaN] as [number, number] }), [g, insuficiente]);
  const legenda = useMemo(() => (g && !insuficiente ? legendaDoModelo(g, classes) : { titulo: '', itens: [] as ItemDaLegenda[] }), [g, insuficiente, classes]);
  // As camadas geotécnicas (§4.6): a unidade de cada nó pela fonte escolhida e o método do modelo; a zona predominante de cada
  // unidade (a cor, a mesma das colunas). Refeitas só quando muda a grade ou a fonte.
  const unidades = useMemo(() => (g && !insuficiente && mapaDasCamadas ? unidadesNaGrade(g, mapaDasCamadas, g.metodoUsado) : null),
    [g, insuficiente, mapaDasCamadas]);
  const zonaDaUnidade = useMemo(() => (mapaDasCamadas ? zonasDasUnidades(mapaDasCamadas, p.verticais) : new Map<string, number | null>()),
    [mapaDasCamadas, p.verticais]);
  const tokensDasUnidades = useMemo(() => (unidades?.unidades ?? []).map((u) => corDaUnidade(u, zonaDaUnidade.get(normalizarUnidade(u)) ?? null)),
    [unidades, zonaDaUnidade]);
  const escondidasChave = aj.unidadesEscondidas.map(normalizarUnidade).sort().join('|');
  // A tela ouve o estado do desenho (sem WebGL, ela sabe).
  const aoEstadoRef = useRef(p.aoEstado);
  aoEstadoRef.current = p.aoEstado;
  useEffect(() => { aoEstadoRef.current?.(estado); }, [estado]);
  // O que o desenho fora do React lê (o ResizeObserver, o quadro): sempre o render mais recente.
  const verticaisRef = useRef(p.verticais);
  verticaisRef.current = p.verticais;
  const planoRef = useRef(plano);
  planoRef.current = plano;

  // A posição de partida das fatias: o meio da grade.
  useEffect(() => {
    if (!g || !g.grade.n.x) return;
    const { origem, passo, n } = g.grade;
    setFatias((f) => ({
      ns: { ...f.ns, posicao: origem.x + Math.floor((n.x - 1) / 2) * passo.x },
      eo: { ...f.eo, posicao: origem.y + Math.floor((n.y - 1) / 2) * passo.y },
      planta: { ...f.planta, posicao: origem.cota + Math.floor((n.z - 1) / 2) * passo.z },
    }));
  }, [g]);

  /* ── O motor do desenho: o three.js, a cena, a câmera, o giro ─────── */

  type Motor = {
    T: Tres; renderer: import('three').WebGLRenderer; cena: import('three').Scene; camera: import('three').PerspectiveCamera;
    grupo: import('three').Group;
    camadas: Partial<Record<'terreno' | 'colunas' | 'cerca' | 'voxels' | 'fitas' | 'malha' | 'agua' | 'unidades' | TipoDeFatia, Objeto3d>>;
    alvo: import('three').Vector3; quadro: number | null; cores: CoresDoModelo;
  };
  const motor = useRef<Motor | null>(null);
  // O estado mais recente, para a ponte e para o desenho fora do React.
  const atual = useRef({
    exagero, opacidade, vista, fatias, estado, satelitePronto, comSatelite, voxels: { n: 0, excedeu: false }, colunas: [] as string[],
    raio: aj.raio,
    paredes: paredesDoFundo(VISTA_DE_PARTIDA.azimute) as ParedeDaMalha[],
    desenhoDasColunas: { forma: aj.forma, raio: aj.raio, cor: corDasColunas, geometria: '' } as { forma: AjustesDo3d['forma']; raio: number; cor: AjustesDo3d['corDasVerticais']; geometria: string },
  });
  atual.current = { ...atual.current, exagero, opacidade, vista, fatias, estado, satelitePronto, comSatelite, raio: aj.raio };

  /**
   * Descarta um objeto e o que ele carrega (geometrias, materiais, texturas) e o próprio objeto quando ele tem
   * `dispose()` — o `InstancedMesh` (colunas e voxels): só o evento `dispose` dele tira da GPU os buffers
   * `instanceMatrix`/`instanceColor`.
   */
  const descartar = (o: Objeto3d | undefined): void => {
    if (!o) return;
    o.removeFromParent();
    o.traverse((x) => {
      const m = x as unknown as { geometry?: { dispose(): void }; material?: unknown; dispose?: () => void };
      m.geometry?.dispose();
      const materiais = Array.isArray(m.material) ? m.material : m.material ? [m.material] : [];
      for (const mat of materiais as { map?: { dispose(): void } | null; dispose(): void }[]) { mat.map?.dispose(); mat.dispose(); }
      if (typeof m.dispose === 'function') m.dispose();
    });
  };

  /** Desenha um quadro (no próximo quadro do navegador), com os rótulos das colunas e o norte. */
  const desenhar = (): void => {
    const m = motor.current;
    if (!m || m.quadro !== null) return;
    m.quadro = requestAnimationFrame(() => {
      m.quadro = null;
      posicionarCamera(m);
      m.renderer.render(m.cena, m.camera);
      atualizarSobreposicao(m);
    });
  };

  const posicionarCamera = (m: Motor): void => {
    const v = atual.current.vista;
    const az = (v.azimute * Math.PI) / 180, inc = (v.inclinacao * Math.PI) / 180;
    // Azimute 0: a câmera ao sul, olhando para o norte (−z).
    m.camera.position.set(
      m.alvo.x - v.distancia * Math.cos(inc) * Math.sin(az),
      m.alvo.y + v.distancia * Math.sin(inc),
      m.alvo.z + v.distancia * Math.cos(inc) * Math.cos(az),
    );
    m.camera.near = Math.max(0.1, v.distancia / 1000);
    m.camera.far = v.distancia * 20;
    m.camera.lookAt(m.alvo);
    m.camera.updateProjectionMatrix();
    // As paredes de fundo da malha: as duas de normal mais alinhada à direção da câmera (trocam no giro).
    const paredes = paredesDoFundo(v.azimute);
    atual.current.paredes = paredes;
    const malha = m.camadas.malha;
    if (malha) for (const f of malha.children) if (f.userData.parede) f.visible = paredes.includes(f.userData.parede as ParedeDaMalha);
  };

  /** O ponto do plano (x leste, y norte, cota) na tela, em px da caixa do canvas (ou do quadro dado); null atrás da câmera. */
  const naTela = (m: Motor, x: number, y: number, cota: number, quadro?: { largura: number; altura: number }): { x: number; y: number } | null => {
    const v = new m.T.Vector3(x, cota, -y);
    m.grupo.localToWorld(v);
    v.project(m.camera);
    if (v.z > 1) return null;
    const el = m.renderer.domElement;
    const w = quadro?.largura ?? el.clientWidth, h = quadro?.altura ?? el.clientHeight;
    return { x: ((v.x + 1) / 2) * w, y: ((1 - v.y) / 2) * h };
  };

  /** O ângulo do norte na tela (rad, horário a partir de «para cima»): o mesmo na tela e na captura. */
  const anguloDoNorte = (m: Motor, quadro?: { largura: number; altura: number }): number | null => {
    const cota = m.alvo.y / atual.current.exagero;
    const a = naTela(m, 0, 0, cota, quadro), b = naTela(m, 0, 10, cota, quadro);
    return a && b ? Math.atan2(b.x - a.x, a.y - b.y) : null;
  };

  const atualizarSobreposicao = (m: Motor): void => {
    const caixaDosRotulos = rotulos.current;
    const vs = verticaisRef.current, pl = planoRef.current;
    if (caixaDosRotulos) {
      Array.from(caixaDosRotulos.children).forEach((filho, i) => {
        const v = vs[i];
        const el = filho as HTMLElement;
        const q = v ? pl.paraPlano(v.latitude, v.longitude) : null;
        const s = q && Number.isFinite(q.x) && Number.isFinite(v.cota) ? naTela(m, q.x, q.y, v.cota) : null;
        el.hidden = !s;
        if (s) el.style.transform = `translate(${Math.round(s.x)}px, ${Math.round(s.y)}px)`;
      });
    }
    // Os rótulos da malha: só os das paredes de fundo, no lugar projetado.
    const caixaDaMalha = rotulosDaMalha.current, daMalha = malhaRef.current;
    if (caixaDaMalha && daMalha) {
      const paredes = atual.current.paredes;
      Array.from(caixaDaMalha.children).forEach((filho, i) => {
        const r = daMalha.rotulos[i];
        const el = filho as HTMLElement;
        const s = r && r.paredes.every((x) => paredes.includes(x)) ? naTela(m, r.posicao[0], r.posicao[1], r.posicao[2]) : null;
        el.hidden = !s;
        if (s) el.style.transform = `translate(${Math.round(s.x)}px, ${Math.round(s.y)}px)`;
      });
    }
    if (norte.current) {
      const angulo = anguloDoNorte(m);
      if (angulo !== null) norte.current.style.transform = `rotate(${angulo}rad)`;
    }
  };

  // Monta o three.js (sob demanda) e a cena; desmonta e descarta tudo ao sair.
  // A cena se refaz quando a grade volta (recálculo, grandeza, método): `carregando` até ela estar montada, e
  // `versaoDaCena` (nas dependências das camadas) faz terreno, colunas, fatias, cerca e voxels voltarem a ela.
  useEffect(() => {
    setEstado('carregando');
    if (!g || insuficiente) return undefined;
    let vivo = true;
    let observador: ResizeObserver | null = null;
    void (async () => {
      let T: Tres;
      try {
        T = await import('three');
      } catch (e) {
        if (vivo) { console.error('o desenho 3D não carregou', e); setErro('O desenho 3D não abriu neste navegador. A seção e a fatia em planta continuam; recarregue a página para tentar de novo.'); setEstado('erro'); }
        return;
      }
      if (!vivo || !tela.current) return;
      let renderer: import('three').WebGLRenderer;
      try {
        renderer = new T.WebGLRenderer({ antialias: true, alpha: true });
      } catch {
        if (vivo) setEstado('sem-webgl');
        return;
      }
      if (!vivo || !tela.current) { renderer.dispose(); return; }
      renderer.setClearColor(new T.Color(0, 0, 0), 0);
      renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
      renderer.domElement.className = 'cptu-3d-canvas';
      tela.current.prepend(renderer.domElement);
      const cena = new T.Scene();
      const camera = new T.PerspectiveCamera(40, 1, 0.1, 10000);
      const grupo = new T.Group();
      cena.add(grupo);
      // Os voxels levam luz (o volume se lê pelas faces); fatias, cerca, colunas e terreno, não.
      cena.add(new T.AmbientLight(new T.Color(1, 1, 1), 0.65));
      const sol = new T.DirectionalLight(new T.Color(1, 1, 1), 0.8);
      sol.position.set(-1, 2, 1.5);
      cena.add(sol);
      const coresLidas = lerCores(caixa.current ?? tela.current);
      motor.current = { T, renderer, cena, camera, grupo, camadas: {}, alvo: new T.Vector3(), quadro: null, cores: coresLidas };
      const medir = (): void => {
        const m = motor.current, el = tela.current;
        if (!m || !el) return;
        const w = Math.max(1, el.clientWidth), h = Math.max(1, el.clientHeight);
        m.renderer.setSize(w, h, false);
        m.renderer.domElement.style.width = '100%';
        m.renderer.domElement.style.height = '100%';
        m.camera.aspect = w / h;
        desenhar();
      };
      medir();
      if (typeof ResizeObserver !== 'undefined') { observador = new ResizeObserver(medir); observador.observe(tela.current); }
      if (!vivo) return;
      setCores(coresLidas);
      setVersaoDaCena((v) => v + 1);
      setEstado('pronto');
    })();
    return () => {
      vivo = false;
      observador?.disconnect();
      const m = motor.current;
      motor.current = null;
      if (!m) return;
      if (m.quadro !== null) cancelAnimationFrame(m.quadro);
      for (const o of Object.values(m.camadas)) descartar(o);
      m.cena.clear();
      m.renderer.dispose();
      m.renderer.forceContextLoss();
      m.renderer.domElement.remove();
    };
    // A cena nasce uma vez por grade que serve; o resto se refaz nos efeitos abaixo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [g === null, insuficiente]);

  /** Troca uma camada da cena (descarta a velha). */
  const trocar = (m: Motor, nome: keyof Motor['camadas'], novo: Objeto3d | undefined): void => {
    descartar(m.camadas[nome]);
    if (novo) { m.grupo.add(novo); m.camadas[nome] = novo; } else delete m.camadas[nome];
  };

  // O enquadramento de partida: o centro da grade e a distância que mostra tudo.
  useEffect(() => {
    const m = motor.current;
    if (estado !== 'pronto' || !m || !g || !g.grade.n.x) return;
    const { origem, passo, n } = g.grade;
    const cx = origem.x + ((n.x - 1) * passo.x) / 2, cy = origem.y + ((n.y - 1) * passo.y) / 2;
    const cotaMeia = origem.cota + ((n.z - 1) * passo.z) / 2;
    m.alvo.set(cx, cotaMeia * exagero, -cy);
    const extensao = Math.max((n.x - 1) * passo.x, (n.y - 1) * passo.y, (n.z - 1) * passo.z * exagero, 10);
    setVista((v) => (v.distancia > 0 ? v : { ...v, distancia: extensao * 1.8 }));
    desenhar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, versaoDaCena, g, exagero]);

  // O terreno (com a imagem ou sem ela) e as colunas.
  useEffect(() => {
    const m = motor.current;
    if (estado !== 'pronto' || !m || !g) return;
    const { T } = m;
    const t = terrenoDoModelo(g, { exagero: 1, plano, limites, comImagem: !!p.imagem });
    const geo = new T.BufferGeometry();
    geo.setAttribute('position', new T.BufferAttribute(t.posicoes, 3));
    if (t.uv) geo.setAttribute('uv', new T.BufferAttribute(t.uv, 2));
    geo.setIndex(new T.BufferAttribute(t.indices, 1));
    let mapa: import('three').Texture | null = null;
    if (p.imagem) {
      mapa = new T.Texture(p.imagem.imagem as unknown as HTMLCanvasElement);
      mapa.flipY = false;
      mapa.colorSpace = T.SRGBColorSpace;
      mapa.needsUpdate = true;
    }
    const corDoTerreno = cinzaDoToken(caixa.current ?? m.renderer.domElement, '--ts-linha-forte');
    const material = new T.MeshBasicMaterial({
      map: mapa, color: mapa ? new T.Color(1, 1, 1) : new T.Color(corDoTerreno[0] / 255, corDoTerreno[1] / 255, corDoTerreno[2] / 255),
      transparent: true, opacity: mapa ? atual.current.opacidade : 0.25, side: T.DoubleSide, depthWrite: false,
    });
    const terreno = new T.Mesh(geo, material);
    terreno.renderOrder = 2;
    terreno.visible = atual.current.comSatelite;
    terreno.name = 'terreno';
    trocar(m, 'terreno', terreno);
    setSatelitePronto(!!mapa);
    desenhar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, versaoDaCena, g, p.imagem, plano, limites]);

  // As colunas (onda 347, §4.1): um cilindro (ou um prisma) por leitura, com o raio dos ajustes, na cor escolhida
  // (a zona SBTn medida, a zona SBT medida, ou a classe da grandeza do modelo nas 7 classes da grade), sem luz.
  useEffect(() => {
    const m = motor.current;
    if (estado !== 'pronto' || !m || !g) return;
    const { T } = m;
    const cols = colunasDoModelo(p.verticais, { exagero: 1, plano });
    const total = cols.reduce((s, c) => s + c.zonas.length, 0);
    const raio = aj.raio;
    const geometria = aj.forma === 'cilindro'
      ? new T.CylinderGeometry(raio, raio, 1, LADOS_DO_CILINDRO)
      : new T.BoxGeometry(2 * raio, 1, 2 * raio);
    const materialDaColuna = new T.MeshBasicMaterial({ color: new T.Color(1, 1, 1) });
    const instancias = new T.InstancedMesh(geometria, materialDaColuna, Math.max(1, total));
    const matriz = new T.Matrix4(), cor = new T.Color();
    const cinza = cinzaDoToken(caixa.current ?? m.renderer.domElement, '--ts-tinta-3');
    let i = 0;
    cols.forEach((c, iv) => {
      const v = p.verticais[iv];
      const sbt = v.zonaSbt;
      const valores = v.colunas[principal];
      for (let s = 0; s < c.zonas.length; s++) {
        const x = c.posicoes[6 * s], topo = c.posicoes[6 * s + 1], base = c.posicoes[6 * s + 4], z = c.posicoes[6 * s + 2];
        const altura = Math.max(1e-3, topo - base);
        matriz.makeScale(1, altura, 1).setPosition(x, (topo + base) / 2, z);
        instancias.setMatrixAt(i, matriz);
        let rgb: Rgb | undefined;
        if (corDasColunas === 'sbt') rgb = sbt ? m.cores.zonas[sbt[s]] : undefined;
        else if (corDasColunas === 'camada') {
          // A unidade da camada da fonte escolhida na profundidade da leitura, na cor da unidade (a zona predominante dela).
          const d = v.z[s];
          // [topo, base), como a grade (`unidadesNaGrade`) e a cor (`zonasDasUnidades`); a base da última fecha a vertical.
          const daVertical = mapaDasCamadas?.get(v.codigo) ?? [];
          const camada = daVertical.find((k, j) => d >= k.topo && (d < k.base || (j === daVertical.length - 1 && d <= k.base)));
          const zona = camada ? zonaDaUnidade.get(normalizarUnidade(camada.unidade)) ?? null : null;
          rgb = zona ? m.cores.zonas[zona] : undefined;
        }
        else if (corDasColunas === 'grandeza') {
          const classe = classeDoValor(valores?.[s] ?? NaN, classes.limites);
          rgb = classe > 0 ? m.cores.escala[classe - 1] : undefined;
        } else rgb = m.cores.zonas[c.zonas[s]];
        rgb ??= cinza;
        instancias.setColorAt(i, cor.setRGB(rgb[0] / 255, rgb[1] / 255, rgb[2] / 255, T.SRGBColorSpace));
        i++;
      }
    });
    instancias.count = total;
    instancias.name = 'colunas';
    opacidadeNaColuna(instancias, opacidadeDasVerticaisRef.current);
    trocar(m, 'colunas', instancias);
    atual.current.colunas = cols.map((c) => c.codigo);
    atual.current.desenhoDasColunas = { forma: aj.forma, raio, cor: corDasColunas, geometria: geometria.type };
    desenhar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, versaoDaCena, g, p.verticais, plano, aj.forma, aj.raio, corDasColunas, classes, principal, mapaDasCamadas, zonaDaUnidade]);

  // A opacidade das verticais (§4.5): só o material (nada se refaz).
  useEffect(() => {
    const c = motor.current?.camadas.colunas as (Objeto3d & { material?: import('three').MeshBasicMaterial }) | undefined;
    if (!c?.material) return;
    opacidadeNaColuna(c as Objeto3d & { material: import('three').MeshBasicMaterial }, aj.opacidadeDasVerticais);
    desenhar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aj.opacidadeDasVerticais, estado, versaoDaCena]);

  // A opacidade e o satélite ligado.
  useEffect(() => {
    const m = motor.current;
    const t = m?.camadas.terreno as (import('three').Mesh & { material: import('three').MeshBasicMaterial }) | undefined;
    if (!m || !t) return;
    if (t.material.map) t.material.opacity = opacidade;
    t.visible = comSatelite;
    desenhar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opacidade, comSatelite, satelitePronto, versaoDaCena]);

  /** Um quadrilátero texturizado (fatia), sem luz, com os pixels RGBA (alfa 0 fora do visível). */
  const quadComTextura = (m: Motor, cantos: Float32Array, rgba: Uint8ClampedArray, largura: number, altura: number): Objeto3d => {
    const { T } = m;
    const geo = new T.BufferGeometry();
    geo.setAttribute('position', new T.BufferAttribute(cantos, 3));
    geo.setAttribute('uv', new T.BufferAttribute(new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]), 2));
    geo.setIndex([0, 3, 1, 1, 3, 2]);
    const textura = new T.DataTexture(new Uint8Array(rgba.buffer, rgba.byteOffset, rgba.byteLength), largura, altura, T.RGBAFormat);
    textura.flipY = false;
    textura.magFilter = T.NearestFilter;
    textura.minFilter = T.NearestFilter;
    textura.colorSpace = T.SRGBColorSpace;
    textura.needsUpdate = true;
    const material = new T.MeshBasicMaterial({ map: textura, transparent: true, alphaTest: 0.5, side: T.DoubleSide });
    return new T.Mesh(geo, material);
  };

  // As fatias.
  useEffect(() => {
    const m = motor.current;
    if (estado !== 'pronto' || !m || !g) return;
    for (const tipo of ['ns', 'eo', 'planta'] as const) {
      const f = fatias[tipo];
      if (!f.ligada) { trocar(m, tipo, undefined); continue; }
      const r = fatiaDoModelo(g, tipo, f.posicao, { exagero: 1, cores: m.cores, limites: classes.limites });
      if (!r.largura) { trocar(m, tipo, undefined); continue; }
      const malha = quadComTextura(m, r.cantos, r.rgba, r.largura, r.altura);
      malha.name = `fatia-${tipo}`;
      malha.userData = { tipo, indice: r.indice };
      trocar(m, tipo, malha);
    }
    desenhar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, versaoDaCena, g, fatias, classes]);

  // A cerca das linhas de corte (a mesma resposta da seção 2D).
  useEffect(() => {
    const m = motor.current;
    if (estado !== 'pronto' || !m) return;
    const { T } = m;
    if (!comCerca || !p.secoes) { trocar(m, 'cerca', undefined); desenhar(); return; }
    const grupo = new T.Group();
    for (const l of p.linhas) {
      const s = p.secoes[l.nome];
      if (!s || s.insuficiente) continue;
      // Os limites da GRADE (os da legenda): a mesma cor vale o mesmo intervalo na cerca e nas fatias. Sem grade, os da seção.
      const c = cercaDoModelo(s, l.pontos, { exagero: 1, plano, cores: m.cores, grandeza: p.grandeza, limites: g ? classes.limites : undefined });
      if (c.largura < 2 || !c.altura) continue;
      const geo = new T.BufferGeometry();
      geo.setAttribute('position', new T.BufferAttribute(c.posicoes, 3));
      geo.setAttribute('uv', new T.BufferAttribute(c.uv, 2));
      geo.setIndex(new T.BufferAttribute(c.indices, 1));
      const textura = new T.DataTexture(new Uint8Array(c.rgba.buffer, c.rgba.byteOffset, c.rgba.byteLength), c.largura, c.altura, T.RGBAFormat);
      textura.flipY = false;
      textura.magFilter = T.NearestFilter;
      textura.minFilter = T.NearestFilter;
      textura.colorSpace = T.SRGBColorSpace;
      textura.needsUpdate = true;
      const malha = new T.Mesh(geo, new T.MeshBasicMaterial({ map: textura, transparent: true, alphaTest: 0.5, side: T.DoubleSide }));
      malha.name = `cerca ${l.nome}`;
      grupo.add(malha);
    }
    trocar(m, 'cerca', grupo.children.length ? grupo : undefined);
    desenhar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, versaoDaCena, comCerca, p.secoes, p.linhas, plano, p.grandeza, g, classes]);

  // Os voxels das zonas (ou classes) escolhidas.
  const voxels = useMemo(() => (g && !insuficiente ? voxelsDoModelo(g, { zonas: zonasDosVoxels, exagero: 1, limites: classes.limites }) : null),
    [g, insuficiente, zonasDosVoxels, classes]);
  useEffect(() => {
    const m = motor.current;
    if (estado !== 'pronto' || !m || !g || !voxels) return;
    const { T } = m;
    atual.current.voxels = { n: voxels.excedeu ? 0 : voxels.n, excedeu: voxels.excedeu };
    if (!voxels.n || voxels.excedeu) { trocar(m, 'voxels', undefined); desenhar(); return; }
    const { passo } = g.grade;
    const caixaDoVoxel = new T.BoxGeometry(passo.x, passo.z, passo.y);
    const instancias = new T.InstancedMesh(caixaDoVoxel, new T.MeshLambertMaterial({ color: new T.Color(1, 1, 1) }), voxels.n);
    const matriz = new T.Matrix4(), cor = new T.Color();
    // A zona só no SBTn; o Su e as contínuas, a classe (a chave dos voxels é a classe 1–7).
    const usaZona = pintaPelaZona(p.grandeza) && !!g.zonas;
    for (let i = 0; i < voxels.n; i++) {
      matriz.makeTranslation(voxels.centros[3 * i], voxels.centros[3 * i + 1], voxels.centros[3 * i + 2]);
      instancias.setMatrixAt(i, matriz);
      const rgb = usaZona ? m.cores.zonas[voxels.zonas[i]] : m.cores.escala[voxels.zonas[i] - 1];
      if (rgb) instancias.setColorAt(i, cor.setRGB(rgb[0] / 255, rgb[1] / 255, rgb[2] / 255, T.SRGBColorSpace));
    }
    instancias.name = 'voxels';
    // A leitura do clique sai do `instanceId` → o centro do voxel (o nó), nunca da posição arredondada na face.
    instancias.userData = { centros: voxels.centros };
    trocar(m, 'voxels', instancias);
    desenhar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, versaoDaCena, g, voxels]);

  // As camadas geotécnicas (§4.6): «Blocos», uma `InstancedMesh` por unidade (como os voxels de hoje, com luz); «Só contatos», as
  // faces entre unidades diferentes, uma malha por unidade (a de baixo), sem luz. Transparentes com a opacidade das camadas, sem
  // escrever a profundidade, desenhadas com as fitas (4). Trocar a fonte, o desenho ou as escondidas refaz e devolve a anterior à GPU;
  // a opacidade só troca no material (abaixo).
  const opacidadeDasCamadasRef = useRef(aj.opacidadeDasCamadas);
  opacidadeDasCamadasRef.current = aj.opacidadeDasCamadas;
  const desenhoDasUnidades = useRef({ voxels: 0, faces: 0, porUnidade: [] as number[], excedeu: false });
  const [camadasExcedem, setCamadasExcedem] = useState(false);
  useEffect(() => {
    const m = motor.current;
    if (estado !== 'pronto' || !m) return;
    desenhoDasUnidades.current = { voxels: 0, faces: 0, porUnidade: (unidades?.unidades ?? []).map(() => 0), excedeu: false };
    setCamadasExcedem(false);
    if (!g || !unidades || !unidades.unidades.length) { trocar(m, 'unidades', undefined); desenhar(); return; }
    const { T } = m;
    const escondidas = new Set(aj.unidadesEscondidas);
    const el = caixa.current ?? m.renderer.domElement;
    const cores = tokensDasUnidades.map((t) => {
      const z = /^--ts-sbtn-(\d)$/.exec(t);
      const c = (z ? m.cores.zonas[Number(z[1])] : undefined) ?? cinzaDoToken(el, t);
      return new T.Color().setRGB(c[0] / 255, c[1] / 255, c[2] / 255, T.SRGBColorSpace);
    });
    const opacidade = opacidadeDasCamadasRef.current;
    const comum = { transparent: true, opacity: opacidade, depthWrite: false };
    const grupo = new T.Group();
    grupo.name = 'unidades';
    const porUnidade = unidades.unidades.map(() => 0);
    if (aj.desenhoDasCamadas === 'blocos') {
      const v = voxelsDeUnidade(g, unidades, { escondidas, exagero: 1 });
      if (v.unidade.length > VOXELS_NO_MAXIMO) {
        desenhoDasUnidades.current = { voxels: 0, faces: 0, porUnidade, excedeu: true };
        setCamadasExcedem(true);
        trocar(m, 'unidades', undefined); desenhar(); return;
      }
      for (let i = 0; i < v.unidade.length; i++) porUnidade[v.unidade[i]]++;
      const { passo } = g.grade;
      const matriz = new T.Matrix4();
      porUnidade.forEach((quantos, un) => {
        if (!quantos) return;
        const inst = new T.InstancedMesh(new T.BoxGeometry(passo.x, passo.z, passo.y), new T.MeshLambertMaterial({ ...comum, color: cores[un] }), quantos);
        let j = 0;
        for (let i = 0; i < v.unidade.length; i++) {
          if (v.unidade[i] !== un) continue;
          inst.setMatrixAt(j++, matriz.makeTranslation(v.centros[3 * i], v.centros[3 * i + 1], v.centros[3 * i + 2]));
        }
        inst.name = `unidade ${unidades.unidades[un]}`;
        inst.renderOrder = ORDEM_DAS_CAMADAS;
        grupo.add(inst);
      });
      desenhoDasUnidades.current = { voxels: v.unidade.length, faces: 0, porUnidade, excedeu: false };
    } else {
      const f = facesDeContato(g, unidades, { escondidas, exagero: 1 });
      for (let i = 0; i < f.unidade.length; i++) porUnidade[f.unidade[i]]++;
      porUnidade.forEach((quantas, un) => {
        if (!quantas) return;
        const pos = new Float32Array(quantas * 18);
        let j = 0;
        for (let i = 0; i < f.unidade.length; i++) {
          if (f.unidade[i] !== un) continue;
          pos.set(f.posicoes.subarray(18 * i, 18 * i + 18), 18 * j++);
        }
        const geo = new T.BufferGeometry();
        geo.setAttribute('position', new T.BufferAttribute(pos, 3));
        const malha = new T.Mesh(geo, new T.MeshBasicMaterial({ ...comum, color: cores[un], side: T.DoubleSide }));
        malha.name = `contato ${unidades.unidades[un]}`;
        malha.renderOrder = ORDEM_DAS_CAMADAS;
        grupo.add(malha);
      });
      desenhoDasUnidades.current = { voxels: 0, faces: f.unidade.length, porUnidade, excedeu: false };
    }
    trocar(m, 'unidades', grupo.children.length ? grupo : undefined);
    desenhar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, versaoDaCena, g, unidades, tokensDasUnidades, aj.desenhoDasCamadas, escondidasChave]);
  const camadasRef = useRef({ fonte, desenho: aj.desenhoDasCamadas, opacidade: aj.opacidadeDasCamadas, unidades, total: g?.verticais.length ?? 0 });
  camadasRef.current = { fonte, desenho: aj.desenhoDasCamadas, opacidade: aj.opacidadeDasCamadas, unidades, total: g?.verticais.length ?? 0 };
  // A opacidade das camadas: só o material (nada se refaz).
  useEffect(() => {
    const u = motor.current?.camadas.unidades;
    if (!u) return;
    u.traverse((x) => {
      const mat = (x as unknown as { material?: { opacity: number } }).material;
      if (mat) mat.opacity = aj.opacidadeDasCamadas;
    });
    desenhar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aj.opacidadeDasCamadas, unidades, estado, versaoDaCena]);

  // As fitas das verticais (o pedido de 16/10): refeitas quando muda o gráfico, as ligadas ou a escala; a opacidade
  // só troca no material (abaixo).
  const sobre = p.sobreposicoes ?? null;
  const ligadasChave = sobre ? [...sobre.ligadas].sort().join('|') : '';
  const largFita = useMemo(() => (g ? larguraDaFita(g.verticais) : 0), [g]);
  const fitas: FitaDaVertical[] = useMemo(
    () => (sobre && g && !insuficiente
      ? fitasDasVerticais(sobre.dados, { plano, ligadas: sobre.ligadas, largura: largFita, exageroHorizontal: aj.exageroHorizontal })
      : []),
    // As ligadas resumidas pela chave: o mesmo conjunto não refaz as fitas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sobre?.dados, ligadasChave, plano, largFita, g, insuficiente, aj.exageroHorizontal],
  );
  const sobreRef = useRef(sobre);
  sobreRef.current = sobre;
  const ladoRef = useRef({ lado: aj.ladoDaFita, raio: aj.raio });
  ladoRef.current = { lado: aj.ladoDaFita, raio: aj.raio };
  /** Põe cada fita no lado escolhido: a direção em planta (x leste, y norte); na cena, z = −y e o giro em y leva o u para lá. */
  const porNoLado = (grupo: Objeto3d): void => {
    const { lado, raio } = ladoRef.current;
    const [dx, dy] = DIRECAO_DO_LADO[lado];
    for (const sub of grupo.children) {
      const u = sub.userData as { x: number; y: number };
      sub.position.set(u.x + dx * raio, 0, -(u.y + dy * raio));
      sub.rotation.y = Math.atan2(dy, dx);
      sub.userData.lado = lado;
    }
  };
  useEffect(() => {
    const m = motor.current;
    if (estado !== 'pronto' || !m) return;
    if (!fitas.length) { trocar(m, 'fitas', undefined); desenhar(); return; }
    const { T } = m;
    const el = caixa.current ?? m.renderer.domElement;
    const paraCor = (c: Rgb): import('three').Color => new T.Color().setRGB(c[0] / 255, c[1] / 255, c[2] / 255, T.SRGBColorSpace);
    const tinta = paraCor(cinzaDoToken(el, '--ts-tinta'));
    const preenchimento = paraCor(cinzaDoToken(el, '--ts-tinta-3'));
    const opacidade = sobreRef.current?.opacidade ?? 1;
    const grupo = new T.Group();
    for (const f of fitas) {
      const sub = new T.Group();
      sub.name = `fita ${f.codigo}`;
      sub.userData = { codigo: f.codigo, tipo: f.tipo, largura: f.largura, x: f.x, y: f.y };
      const geo = new T.BufferGeometry();
      geo.setAttribute('position', new T.BufferAttribute(f.posicoes, 3));
      geo.setIndex(new T.BufferAttribute(f.indices, 1));
      let material: import('three').MeshBasicMaterial;
      if (f.zonas) {
        const cores = new Float32Array(f.zonas.length * 3);
        const cinza = cinzaDoToken(el, '--ts-tinta-3');
        for (let i = 0; i < f.zonas.length; i++) {
          const c = paraCor(m.cores.zonas[f.zonas[i]] ?? cinza);
          cores.set([c.r, c.g, c.b], 3 * i);
        }
        geo.setAttribute('color', new T.BufferAttribute(cores, 3));
        material = new T.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: opacidade, side: T.DoubleSide, depthWrite: false });
      } else {
        material = new T.MeshBasicMaterial({ color: preenchimento, transparent: true, opacity: opacidade, side: T.DoubleSide, depthWrite: false });
      }
      const malha = new T.Mesh(geo, material);
      malha.name = 'fita';
      malha.renderOrder = 4;
      // Fina e em pé: desenha sempre (como antes).
      malha.frustumCulled = false;
      sub.add(malha);
      const linhas = (pos: Float32Array, nome: string): void => {
        if (!pos.length) return;
        const gl = new T.BufferGeometry();
        gl.setAttribute('position', new T.BufferAttribute(pos, 3));
        const l = new T.LineSegments(gl, new T.LineBasicMaterial({ color: tinta, transparent: true, opacity: opacidade }));
        l.name = nome;
        l.renderOrder = 5;
        l.frustumCulled = false;
        sub.add(l);
      };
      linhas(f.traco, 'traco');
      linhas(f.moldura, 'moldura');
      grupo.add(sub);
    }
    grupo.name = 'fitas';
    porNoLado(grupo);
    trocar(m, 'fitas', grupo);
    desenhar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, versaoDaCena, fitas]);
  // O lado da fita (§4.7): u = 0 na face da coluna (o raio) no lado escolhido, u para fora; só a posição e o giro de cada fita.
  useEffect(() => {
    const f = motor.current?.camadas.fitas;
    if (!f) return;
    porNoLado(f);
    desenhar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aj.ladoDaFita, aj.raio, fitas, estado, versaoDaCena]);
  // A opacidade das fitas: só o material (nada se refaz).
  useEffect(() => {
    const f = motor.current?.camadas.fitas;
    if (!f || !sobre) return;
    f.traverse((x) => {
      const mat = (x as unknown as { material?: { opacity: number } }).material;
      if (mat) mat.opacity = sobre.opacidade;
    });
    desenhar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sobre?.opacidade, fitas, estado, versaoDaCena]);

  // O exagero vertical: a escala do grupo inteiro (terreno, colunas, fatias, cerca e voxels juntos).
  useEffect(() => {
    const m = motor.current;
    if (!m) return;
    m.grupo.scale.set(1, exagero, 1);
    desenhar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exagero, estado, versaoDaCena]);

  // A malha de cota e coordenadas (§4.2): refeita só quando muda a grade, o plano ou o sistema; ligar e desligar troca a camada
  // (a desligada devolve à GPU a geometria e o material). As paredes à vista se escolhem a cada quadro (`posicionarCamera`).
  const sistema = p.sistema ?? { tipo: 'graus' as const };
  const fusoDaMalha = sistema.tipo === 'utm' ? sistema.fuso : null;
  const malha: MalhaDoModelo | null = useMemo(() => (g && !insuficiente ? linhasDaMalha(g, plano, sistema.tipo, fusoDaMalha) : null),
    [g, insuficiente, plano, sistema.tipo, fusoDaMalha]);
  const malhaRef = useRef<MalhaDoModelo | null>(null);
  malhaRef.current = aj.malha ? malha : null;
  const sistemaRef = useRef(sistema);
  sistemaRef.current = sistema;
  useEffect(() => {
    const m = motor.current;
    if (estado !== 'pronto' || !m) return;
    if (!aj.malha || !malha) { trocar(m, 'malha', undefined); desenhar(); return; }
    const { T } = m;
    const tinta = cinzaDoToken(caixa.current ?? m.renderer.domElement, '--ts-linha');
    const cor = new T.Color().setRGB(tinta[0] / 255, tinta[1] / 255, tinta[2] / 255, T.SRGBColorSpace);
    const grupo = new T.Group();
    grupo.name = 'malha';
    const linhas = (pos: Float32Array, nome: string, parede: ParedeDaMalha | null): void => {
      const geo = new T.BufferGeometry();
      geo.setAttribute('position', new T.BufferAttribute(pos, 3));
      const l = new T.LineSegments(geo, new T.LineBasicMaterial({ color: cor }));
      l.name = nome;
      l.userData = { parede };
      l.renderOrder = 1;
      if (parede) l.visible = atual.current.paredes.includes(parede);
      grupo.add(l);
    };
    linhas(malha.piso, 'piso', null);
    for (const k of ['n', 's', 'l', 'o'] as const) linhas(malha.paredes[k], `parede-${k}`, k);
    trocar(m, 'malha', grupo);
    desenhar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, versaoDaCena, malha, aj.malha]);

  // A superfície do nível d'água (§4.3): a cota do NA (boca − NA da premissa) nas verticais que servem, entre elas pelo método do
  // modelo, só dentro do fecho; refeita só quando muda a grade ou o NA das verticais. A cor é a do token `--ts-agua`, com a
  // opacidade dos ajustes (só o material muda); a borda e um anel no NA de cada vertical vão opacos, na mesma tinta.
  // O pedido sabe do NA? `na === undefined` em todas = não sabe (o pacote do Portal emitido antes do campo): a água some da
  // barra e do desenho, sem dizer «nenhuma vertical tem NA» (seria falso). `null` = a vertical sem NA, e a frase vale.
  const sabeDoNa = p.verticais.some((v) => v.na !== undefined);
  const nasDasVerticais = p.verticais.map((v) => `${v.codigo}:${v.na === undefined ? '?' : v.na ?? ''}`).join('|');
  const agua: SuperficieDoNa | null = useMemo(() => {
    if (!g || insuficiente || !sabeDoNa) return null;
    const modelo = g.variogramas.find((v) => v.coluna === principal)?.ajustado.modelo ?? 'esferico';
    return superficieDoNa(g, p.verticais.map((v) => ({ codigo: v.codigo, na: v.na ?? null })), g.metodoUsado, modelo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [g, insuficiente, principal, nasDasVerticais]);
  // Alguma célula da grade com os quatro cantos estimados (no fecho)? Sem nenhuma, só os anéis: a superfície não se desenha.
  const aguaTemCelula = useMemo(() => {
    if (!g || !agua || !('cotas' in agua)) return false;
    const { x: nx, y: ny } = g.grade.n;
    for (let iy = 0; iy + 1 < ny; iy++) {
      for (let ix = 0; ix + 1 < nx; ix++) {
        const a = ix + nx * iy;
        if ([a, a + 1, a + nx, a + nx + 1].every((k) => Number.isFinite(agua.cotas[k]))) return true;
      }
    }
    return false;
  }, [g, agua]);
  const aguaRef = useRef({ agua, opacidade: aj.opacidadeDaAgua });
  aguaRef.current = { agua, opacidade: aj.opacidadeDaAgua };
  useEffect(() => {
    const m = motor.current;
    if (estado !== 'pronto' || !m) return;
    if (!aj.superficieDoNa || !g || !agua || !('cotas' in agua)) { trocar(m, 'agua', undefined); desenhar(); return; }
    const { T } = m;
    const { origem, passo, n } = g.grade;
    const { cotas } = agua;
    // Os nós em planta (x, cota, −y) e dois triângulos por célula com os quatro cantos estimados.
    const posicoes = new Float32Array(n.x * n.y * 3);
    for (let iy = 0; iy < n.y; iy++) {
      for (let ix = 0; ix < n.x; ix++) {
        const k = ix + n.x * iy;
        posicoes.set([origem.x + ix * passo.x, Number.isFinite(cotas[k]) ? cotas[k] : 0, -(origem.y + iy * passo.y)], 3 * k);
      }
    }
    const indices: number[] = [];
    const usos = new Map<string, number>();
    const aresta = (a: number, b: number): void => { const c = a < b ? `${a}-${b}` : `${b}-${a}`; usos.set(c, (usos.get(c) ?? 0) + 1); };
    const triangulo = (a: number, b: number, c: number): void => { indices.push(a, b, c); aresta(a, b); aresta(b, c); aresta(c, a); };
    for (let iy = 0; iy + 1 < n.y; iy++) {
      for (let ix = 0; ix + 1 < n.x; ix++) {
        const a = ix + n.x * iy, b = a + 1, c = a + n.x, d = c + 1;
        if (![a, b, c, d].every((k) => Number.isFinite(cotas[k]))) continue;
        triangulo(a, b, d);
        triangulo(a, d, c);
      }
    }
    const tinta = cinzaDoToken(caixa.current ?? m.renderer.domElement, '--ts-agua');
    const cor = new T.Color().setRGB(tinta[0] / 255, tinta[1] / 255, tinta[2] / 255, T.SRGBColorSpace);
    const grupo = new T.Group();
    grupo.name = 'agua';
    if (indices.length) {
      const geo = new T.BufferGeometry();
      geo.setAttribute('position', new T.BufferAttribute(posicoes, 3));
      geo.setIndex(indices);
      const superficie = new T.Mesh(geo, new T.MeshBasicMaterial({
        color: cor, transparent: true, opacity: aguaRef.current.opacidade, side: T.DoubleSide, depthWrite: false,
      }));
      superficie.name = 'superficie';
      superficie.renderOrder = 3;
      grupo.add(superficie);
      // A borda: as arestas de um triângulo só.
      const borda: number[] = [];
      for (const [c, u] of usos) {
        if (u !== 1) continue;
        for (const k of c.split('-').map(Number)) borda.push(posicoes[3 * k], posicoes[3 * k + 1], posicoes[3 * k + 2]);
      }
      const geoBorda = new T.BufferGeometry();
      geoBorda.setAttribute('position', new T.BufferAttribute(new Float32Array(borda), 3));
      const l = new T.LineSegments(geoBorda, new T.LineBasicMaterial({ color: cor }));
      l.name = 'borda';
      grupo.add(l);
    }
    // Um anel no NA de cada vertical que entrou, um pouco maior que a coluna (desenho, não conta): 1,6 × o raio —
    // ESCOLHA DA TELA, SEM FONTE [3d.na-anel] (na lista `ESCOLHAS_DO_3D`).
    const raioDoAnel = 1.6 * aj.raio; // ESCOLHA DA TELA, SEM FONTE [3d.na-anel]
    const daGrade = new Map(g.verticais.map((v) => [v.codigo, v]));
    const nasPorCodigo = new Map(p.verticais.map((v) => [v.codigo, v.na ?? null]));
    for (const codigo of agua.usadas) {
      const v = daGrade.get(codigo), na = nasPorCodigo.get(codigo);
      if (!v || typeof na !== 'number') continue;
      const pontos = Array.from({ length: LADOS_DO_CILINDRO }, (_, i) => {
        const t = (2 * Math.PI * i) / LADOS_DO_CILINDRO;
        return new T.Vector3(v.x + raioDoAnel * Math.cos(t), v.boca - na, -(v.y + raioDoAnel * Math.sin(t)));
      });
      const anel = new T.LineLoop(new T.BufferGeometry().setFromPoints(pontos), new T.LineBasicMaterial({ color: cor }));
      anel.name = `anel ${codigo}`;
      grupo.add(anel);
    }
    trocar(m, 'agua', grupo);
    desenhar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, versaoDaCena, agua, aj.superficieDoNa, aj.raio]);
  // A opacidade da água: só o material da superfície (nada se refaz).
  useEffect(() => {
    const s = motor.current?.camadas.agua?.getObjectByName('superficie') as (Objeto3d & { material?: { opacity: number } }) | undefined;
    if (!s?.material) return;
    s.material.opacity = aj.opacidadeDaAgua;
    desenhar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aj.opacidadeDaAgua, agua, estado, versaoDaCena]);

  // A câmera.
  useEffect(() => { desenhar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [vista]);

  /* ── O giro: ponteiro, roda e teclado ─────────────────────────────── */

  const girar = (dAz: number, dInc: number, fatorDist = 1): void => setVista((v) => ({
    azimute: ((v.azimute + dAz) % 360 + 360) % 360,
    inclinacao: Math.min(89, Math.max(5, v.inclinacao + dInc)),
    distancia: v.distancia * fatorDist,
  }));
  const voltar = (): void => {
    const m = motor.current;
    if (!m || !g) return;
    const { passo, n } = g.grade;
    const extensao = Math.max((n.x - 1) * passo.x, (n.y - 1) * passo.y, (n.z - 1) * passo.z * exagero, 10);
    setVista({ ...VISTA_DE_PARTIDA, distancia: extensao * 1.8 });
  };
  const aoTeclar = (e: EventoDeTecla): void => {
    const acoes: Record<string, () => void> = {
      ArrowLeft: () => girar(-GIRO, 0), ArrowRight: () => girar(GIRO, 0),
      ArrowUp: () => girar(0, GIRO), ArrowDown: () => girar(0, -GIRO),
      '+': () => girar(0, 0, 1 / ZOOM), '=': () => girar(0, 0, 1 / ZOOM), '-': () => girar(0, 0, ZOOM),
      Home: voltar,
    };
    const f = acoes[e.key];
    if (!f) return;
    e.preventDefault();
    f();
  };

  const arrasto = useRef<{ x: number; y: number; moveu: boolean; id: number } | null>(null);
  const aoApertar = (e: EventoDePonteiro<HTMLDivElement>): void => {
    if (e.button !== 0) return;
    arrasto.current = { x: e.clientX, y: e.clientY, moveu: false, id: e.pointerId };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const aoMover = (e: EventoDePonteiro<HTMLDivElement>): void => {
    const a = arrasto.current;
    if (!a || a.id !== e.pointerId) return;
    const dx = e.clientX - a.x, dy = e.clientY - a.y;
    if (!a.moveu && Math.hypot(dx, dy) < 4) return;
    a.moveu = true;
    a.x = e.clientX; a.y = e.clientY;
    girar(dx * 0.4, dy * 0.3);
  };
  const aoSoltar = (e: EventoDePonteiro<HTMLDivElement>): void => {
    const a = arrasto.current;
    arrasto.current = null;
    if (!a || a.moveu) return;
    // Em relação ao CANVAS (dentro da borda da caixa), o mesmo quadro que a câmera projeta.
    const r = (motor.current?.renderer.domElement ?? e.currentTarget).getBoundingClientRect();
    setLeitura(lerNoPonto(e.clientX - r.left, e.clientY - r.top));
  };
  // A roda aproxima e afasta (ouvinte nativo, não passivo: a página não rola por baixo do modelo).
  useEffect(() => {
    const el = tela.current;
    if (!el || estado !== 'pronto') return undefined;
    const roda = (e: WheelEvent): void => { e.preventDefault(); girar(0, 0, e.deltaY > 0 ? ZOOM : 1 / ZOOM); };
    el.addEventListener('wheel', roda, { passive: false });
    return () => el.removeEventListener('wheel', roda);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, versaoDaCena]);

  /** A leitura do ponto clicado: a coluna (medido) ou a fatia, a cerca e o voxel (estimado). */
  const lerNoPonto = (px: number, py: number): string | null => {
    const m = motor.current;
    if (!m) return null;
    posicionarCamera(m);
    m.camera.updateMatrixWorld();
    m.grupo.updateMatrixWorld();
    const el = m.renderer.domElement;
    const raio = new m.T.Raycaster();
    raio.setFromCamera(new m.T.Vector2((px / el.clientWidth) * 2 - 1, -(py / el.clientHeight) * 2 + 1), m.camera);
    // As colunas: a ESCOLHA é a coluna cujo cilindro (o raio dos ajustes — onda 347; no prisma, o cilindro inscrito) o
    // raio da câmera atravessa — pela lateral, no trecho medido, ou pelos discos do topo e da base —, a mais perto da
    // câmera (o menor t entre as faces e entre as colunas: o topo da coluna da frente esconde a lateral da de trás); sem
    // nenhuma, a do segmento projetado mais perto do clique, a até 6 px na tela (a coluna fina, de longe). A COTA é a do
    // ponto onde o raio da câmera entra no cilindro (a face que se vê; no disco do topo, a primeira leitura; no da base, a
    // última); no recurso dos 6 px, a do ponto do eixo mais perto do raio (raio × segmento em 3D) — a fração na tela não
    // é a fração em profundidade numa câmera em perspectiva.
    const raioDaColuna = atual.current.raio;
    let melhor = { i: -1, d: Infinity };
    let atravessada = { i: -1, profundidade: Infinity, y: NaN };
    const { origin: o, direction: dir } = raio.ray;
    for (let i = 0; i < p.verticais.length; i++) {
      const v = p.verticais[i];
      if (!v.z.length) continue;
      const q = plano.paraPlano(v.latitude, v.longitude);
      const topo = m.grupo.localToWorld(new m.T.Vector3(q.x, v.cota - v.z[0], -q.y));
      const base = m.grupo.localToWorld(new m.T.Vector3(q.x, v.cota - v.z[v.z.length - 1], -q.y));
      // A entrada do raio no cilindro de eixo vertical: o exagero só estica o y do grupo, o raio em planta é o mesmo no mundo.
      const ex = o.x - topo.x, ez = o.z - topo.z;
      const qa = dir.x * dir.x + dir.z * dir.z, qb = 2 * (ex * dir.x + ez * dir.z), qc = ex * ex + ez * ez - raioDaColuna * raioDaColuna;
      const disc = qb * qb - 4 * qa * qc;
      if (qa > 0 && disc >= 0) {
        const t = (-qb - Math.sqrt(disc)) / (2 * qa);
        const y = o.y + t * dir.y;
        if (t > 0 && y <= topo.y && y >= base.y && t < atravessada.profundidade) atravessada = { i, profundidade: t, y };
      }
      // Os discos do topo e da base: o plano y = topo.y (ou base.y), dentro do raio em planta.
      if (dir.y !== 0) {
        for (const yDoDisco of [topo.y, base.y]) {
          const t = (yDoDisco - o.y) / dir.y;
          if (!(t > 0) || t >= atravessada.profundidade) continue;
          const hx = o.x + t * dir.x - topo.x, hz = o.z + t * dir.z - topo.z;
          if (hx * hx + hz * hz <= raioDaColuna * raioDaColuna) atravessada = { i, profundidade: t, y: yDoDisco };
        }
      }
      const a = naTela(m, q.x, q.y, v.cota - v.z[0]), b = naTela(m, q.x, q.y, v.cota - v.z[v.z.length - 1]);
      if (!a || !b) continue;
      const dx = b.x - a.x, dy = b.y - a.y, l2 = dx * dx + dy * dy;
      const t = l2 > 0 ? Math.min(Math.max(((px - a.x) * dx + (py - a.y) * dy) / l2, 0), 1) : 0;
      const d = Math.hypot(px - (a.x + t * dx), py - (a.y + t * dy));
      if (d <= 6 && d < melhor.d) melhor = { i, d };
    }
    if (atravessada.i >= 0) melhor = { i: atravessada.i, d: 0 };
    if (melhor.i >= 0) {
      const v = p.verticais[melhor.i];
      const q = plano.paraPlano(v.latitude, v.longitude);
      const topo = m.grupo.localToWorld(new m.T.Vector3(q.x, v.cota - v.z[0], -q.y));
      const base = m.grupo.localToWorld(new m.T.Vector3(q.x, v.cota - v.z[v.z.length - 1], -q.y));
      const naColuna = new m.T.Vector3();
      if (atravessada.i === melhor.i) naColuna.set(topo.x, atravessada.y, topo.z);
      else raio.ray.distanceSqToSegment(topo, base, undefined, naColuna);
      const cotaNoRaio = m.grupo.worldToLocal(naColuna).y;
      const prof = Math.min(Math.max(v.cota - cotaNoRaio, v.z[0]), v.z[v.z.length - 1]);
      const k = indiceNaProfundidade(v.z, prof);
      if (k < 0) return null;
      const cota = v.cota - v.z[k];
      const zona = v.zonaMedida[k] ?? 0;
      const valor = v.colunas[principal]?.[k];
      const grandeza = p.grandeza === 'sbtn'
        ? `${textoDaZona(zona)} (medido)${Number.isFinite(valor) ? ` · Ic ${numeroBr(valor as number, 2)}` : ''}`
        : `${def.rotulo} ${numeroBr(valor ?? NaN, 2)}${def.unidade ? ` ${def.unidade}` : ''} (medido)`;
      // Pintada pelo SBT, a zona SBT vai escrita também (a cor nunca vai sozinha).
      const zonaSbt = atual.current.desenhoDasColunas.cor === 'sbt' ? v.zonaSbt?.[k] ?? 0 : null;
      const sbt = zonaSbt === null ? '' : ` · ${ZONAS[zonaSbt] ? `SBT ${zonaSbt} · ${ZONAS[zonaSbt].nome}` : 'sem zona SBT'}`;
      return `${v.codigo} · cota ${numeroBr(cota, 2)} m · ${grandeza}${sbt}`;
    }
    // A fatia e o voxel: o raio da câmera.
    if (!g) return null;
    const alvos = (['ns', 'eo', 'planta', 'voxels'] as const).map((n) => m.camadas[n]).filter((o): o is Objeto3d => !!o);
    for (const acerto of raio.intersectObjects(alvos, false)) {
      // No voxel, o centro da instância acertada (o nó); na fatia, o ponto do plano (que já passa pelo nó).
      const centros = (acerto.object.userData as { centros?: Float32Array } | undefined)?.centros;
      const id = acerto.instanceId;
      const local = centros && id !== undefined && 3 * id + 2 < centros.length
        ? new m.T.Vector3(centros[3 * id], centros[3 * id + 1], centros[3 * id + 2])
        : m.grupo.worldToLocal(acerto.point.clone());
      const { origem, passo, n } = g.grade;
      const ix = Math.round((local.x - origem.x) / passo.x), iy = Math.round((-local.z - origem.y) / passo.y);
      const iz = Math.round((local.y - origem.cota) / passo.z);
      if (ix < 0 || iy < 0 || iz < 0 || ix >= n.x || iy >= n.y || iz >= n.z) continue;
      const k = ix + n.x * (iy + n.y * iz);
      if (!g.visivel[k]) continue;
      const cota = origem.cota + iz * passo.z;
      const lugar = `${numeroBr(origem.x + ix * passo.x, 1)} m E · ${numeroBr(origem.y + iy * passo.y, 1)} m N · cota ${numeroBr(cota, 2)} m`;
      const valor = g.valores[principal]?.[k];
      const grandeza = g.zonas && pintaPelaZona(p.grandeza)
        ? `${textoDaZona(g.zonas[k])} (estimado) · Ic ${numeroBr(valor ?? NaN, 2)}`
        : `${def.rotulo} ${numeroBr(valor ?? NaN, 2)}${def.unidade ? ` ${def.unidade}` : ''} (estimado)`;
      const classe = pintaPelaZona(p.grandeza) ? 0 : classeDoValor(valor ?? NaN, classes.limites);
      return `${lugar} · ${grandeza}${classe > 0 ? ` · classe ${classe}` : ''}`;
    }
    return null;
  };

  /* ── A ponte para os testes e a captura ───────────────────────────── */

  useEffect(() => {
    const el = caixa.current;
    if (!el) return undefined;
    const ponte = {
      pronto: () => atual.current.estado === 'pronto' && !!motor.current?.camadas.colunas,
      webgl: () => atual.current.estado !== 'sem-webgl' && (atual.current.estado !== 'carregando' || !!motor.current),
      // A lista dos códigos (como antes), com o desenho das colunas junto (onda 347): a forma, o raio, a cor e o tipo da
      // geometria (`CylinderGeometry` ou `BoxGeometry`).
      colunas: () => {
        // E o material (§4.5): transparente, a opacidade e se escreve a profundidade.
        const c = motor.current?.camadas.colunas as (Objeto3d & { material?: { transparent: boolean; opacity: number; depthWrite: boolean } }) | undefined;
        const material = c?.material
          ? { transparent: c.material.transparent, opacity: c.material.opacity, depthWrite: c.material.depthWrite, renderOrder: c.renderOrder }
          : null;
        return Object.assign([...atual.current.colunas], { ...atual.current.desenhoDasColunas, material });
      },
      fatias: () => Object.fromEntries((['ns', 'eo', 'planta'] as const).map((t) => [t, {
        ligada: atual.current.fatias[t].ligada, posicao: atual.current.fatias[t].posicao,
        indice: (motor.current?.camadas[t]?.userData as { indice?: number } | undefined)?.indice ?? -1,
      }])),
      voxels: () => ({ ...atual.current.voxels }),
      satelite: () => {
        const t = motor.current?.camadas.terreno as (Objeto3d & { material?: { opacity: number; map: unknown } }) | undefined;
        return {
          carregada: atual.current.satelitePronto, opacidade: atual.current.opacidade, ligada: atual.current.comSatelite,
          // O que está no material do terreno (com a imagem, a opacidade do satélite).
          material: t?.material ? { opacidade: t.material.opacity, comImagem: !!t.material.map } : null,
          terreno: !!t,
        };
      },
      exagero: () => atual.current.exagero,
      escalaVertical: () => motor.current?.grupo.scale.y ?? NaN,
      // O que está na GPU agora: desenha o quadro na hora (o three só sobe a geometria ao desenhar) e conta.
      memoria: () => {
        const m = motor.current;
        if (!m) return null;
        posicionarCamera(m);
        m.renderer.render(m.cena, m.camera);
        return { geometrias: m.renderer.info.memory.geometries, texturas: m.renderer.info.memory.textures };
      },
      sobreposicoes: (): FitaDaPonte[] => {
        const m = motor.current;
        const camada = m?.camadas.fitas;
        const s = sobreRef.current;
        if (!m || !camada || !s) return [];
        camada.updateMatrixWorld(true);
        // Um ponto da fita (u, cota) no plano (x leste, y norte): pela matriz da fita, de volta às coordenadas do grupo.
        const noPlano = (sub: Objeto3d, u: number): { x: number; y: number } => {
          const v = m.grupo.worldToLocal(sub.localToWorld(new m.T.Vector3(u, 0, 0)));
          return { x: v.x, y: -v.z };
        };
        return camada.children.map((sub) => {
          const u = sub.userData as { codigo: string; tipo: 'serie' | 'zona'; largura: number; x: number; y: number; lado: AjustesDo3d['ladoDaFita'] };
          const malha = sub.children.find((c) => c.name === 'fita') as (Objeto3d & {
            material: { opacity: number; color: import('three').Color }; geometry: import('three').BufferGeometry;
          }) | undefined;
          const faixa: SobreposicaoDaVertical | undefined = s.dados.verticais.find((v) => v.codigo === u.codigo);
          return {
            codigo: u.codigo, grandeza: s.dados.grandeza.chave, tipo: u.tipo, opacidade: malha?.material.opacity ?? NaN, largura: u.largura,
            lado: u.lado, coluna: { x: u.x, y: u.y }, base: noPlano(sub, 0), ponta: noPlano(sub, u.largura),
            naCota(cota: number) {
              if (!faixa || !faixa.cotas.length) return NaN;
              let melhor = 0;
              for (let i = 1; i < faixa.cotas.length; i++) if (Math.abs(faixa.cotas[i] - cota) < Math.abs(faixa.cotas[melhor] - cota)) melhor = i;
              return faixa.valores[melhor];
            },
            larguraNaCota(cota: number) {
              if (!malha || u.tipo !== 'serie') return NaN;
              const pos = malha.geometry.getAttribute('position');
              // Na série, a leitura i é o par de vértices (eixo, ponta): a ponta é o vértice 2i + 1.
              let melhor = -1;
              for (let i = 1; i < pos.count; i += 2) {
                if (melhor < 0 || Math.abs(pos.getY(i) - cota) < Math.abs(pos.getY(melhor) - cota)) melhor = i;
              }
              return melhor < 0 ? NaN : pos.getX(melhor) * (malha.scale?.x ?? 1);
            },
            corNaCota(cota: number) {
              if (!malha) return null;
              const paraRgb = (c: import('three').Color): [number, number, number] => {
                const s2 = { r: 0, g: 0, b: 0 };
                c.getRGB(s2, m.T.SRGBColorSpace);
                return [Math.round(s2.r * 255), Math.round(s2.g * 255), Math.round(s2.b * 255)];
              };
              const cor = malha.geometry.getAttribute('color');
              if (!cor) return paraRgb(malha.material.color);
              const pos = malha.geometry.getAttribute('position');
              for (let i = 0; i + 3 < pos.count; i += 4) {
                const topo = pos.getY(i), base = pos.getY(i + 2);
                if (cota <= topo && cota >= base) return paraRgb(new m.T.Color(cor.getX(i), cor.getY(i), cor.getZ(i)));
              }
              return null;
            },
          };
        });
      },
      // As fitas (onda 347): as mesmas `sobreposicoes`, com o nome do pedido.
      fitas: (): FitaDaPonte[] => ponte.sobreposicoes(),
      // A caixa da geometria do terreno (no grupo, sem o exagero vertical): para ver que o exagero horizontal não o muda.
      terreno: () => {
        const t = motor.current?.camadas.terreno as (Objeto3d & { geometry?: import('three').BufferGeometry }) | undefined;
        if (!t?.geometry) return null;
        t.geometry.computeBoundingBox();
        const b = t.geometry.boundingBox;
        return b ? { min: [b.min.x, b.min.y, b.min.z], max: [b.max.x, b.max.y, b.max.z] } : null;
      },
      camera: () => ({ ...atual.current.vista }),
      // A malha (§4.2): ligada, as paredes de fundo pela vista, as que estão na cena à vista, a distância da câmera ao centro de
      // cada parede (m, no mundo), os intervalos, a extensão da caixa e o sistema dos rótulos.
      malha: () => {
        const m = motor.current;
        const camada = m?.camadas.malha;
        const daMalha = malhaRef.current;
        // A câmera na vista de agora e os rótulos no lugar (o quadro seguinte faria o mesmo; o navegador sem tela adia os quadros):
        // as paredes à vista, os rótulos delas e a matriz do mundo em dia.
        if (m) {
          posicionarCamera(m);
          m.camera.updateMatrixWorld();
          m.grupo.updateMatrixWorld();
          atualizarSobreposicao(m);
        }
        const desenhadas = camada ? camada.children.filter((f) => f.userData.parede && f.visible).map((f) => f.userData.parede as ParedeDaMalha) : [];
        const distancias: Partial<Record<ParedeDaMalha, number>> = {};
        if (m && g) {
          m.grupo.updateMatrixWorld();
          const { origem, passo, n } = g.grade;
          const x0 = origem.x, x1 = origem.x + (n.x - 1) * passo.x, y0 = origem.y, y1 = origem.y + (n.y - 1) * passo.y;
          const zm = origem.cota + ((n.z - 1) * passo.z) / 2, xm = (x0 + x1) / 2, ym = (y0 + y1) / 2;
          const centros: Record<ParedeDaMalha, [number, number]> = { n: [xm, y1], s: [xm, y0], l: [x1, ym], o: [x0, ym] };
          for (const [k, [x, y]] of Object.entries(centros) as [ParedeDaMalha, [number, number]][]) {
            distancias[k] = m.grupo.localToWorld(new m.T.Vector3(x, zm, -y)).distanceTo(m.camera.position);
          }
        }
        return {
          ligada: !!camada && !!daMalha, paredes: [...atual.current.paredes], desenhadas, distancias,
          intervalos: daMalha ? { ...daMalha.intervalos } : null, extensao: daMalha ? { ...daMalha.extensao } : null,
          sistema: sistemaRef.current.tipo,
        };
      },
      // A superfície do nível d'água (§4.3): desenhada (na cena), a opacidade do material (ou a dos ajustes, sem desenho), a ordem de desenho, as
      // verticais que entraram e, sem superfície, por quê.
      agua: () => {
        const { agua: a, opacidade } = aguaRef.current;
        const s = motor.current?.camadas.agua?.getObjectByName('superficie') as (Objeto3d & { material?: { opacity: number } }) | undefined;
        // E a cota da superfície no nó estimado (no fecho) mais perto de cada vertical que entrou (m; o que a simulação do Portal
        // muda, R3). A vertical na borda do fecho pode ter o nó dela fora: vale o estimado mais perto.
        const nos: Record<string, number> = {};
        if (g && a && 'cotas' in a) {
          const { origem, passo, n } = g.grade;
          const daGrade = new Map(g.verticais.map((v) => [v.codigo, v]));
          for (const codigo of a.usadas) {
            const v = daGrade.get(codigo);
            if (!v) continue;
            let melhor = Infinity;
            for (let iy = 0; iy < n.y; iy++) {
              for (let ix = 0; ix < n.x; ix++) {
                const c = a.cotas[ix + n.x * iy];
                const d = Math.hypot(origem.x + ix * passo.x - v.x, origem.y + iy * passo.y - v.y);
                if (Number.isFinite(c) && d < melhor) { melhor = d; nos[codigo] = c; }
              }
            }
          }
        }
        return {
          desenhada: !!s, opacidade: s?.material ? s.material.opacity : opacidade, ordem: s ? s.renderOrder : null,
          usadas: a && 'cotas' in a ? [...a.usadas] : [], motivo: a && 'motivo' in a ? a.motivo : null, nos,
        };
      },
      // As camadas geotécnicas (§4.6): a fonte, o desenho, as unidades (os nomes, da mais rasa à mais funda), a opacidade do material
      // (ou a dos ajustes, sem desenho), quantos voxels e faces estão na cena (e por unidade), quem tem camada e se é sugestão (RN-269).
      camadas: () => {
        const c = camadasRef.current, d = desenhoDasUnidades.current;
        let opacidade = c.opacidade;
        motor.current?.camadas.unidades?.traverse((x) => {
          const mat = (x as unknown as { material?: { opacity: number } }).material;
          if (mat) opacidade = mat.opacity;
        });
        return {
          fonte: c.fonte, desenho: c.desenho, unidades: [...(c.unidades?.unidades ?? [])], opacidade, voxels: d.voxels, faces: d.faces,
          porUnidade: [...d.porUnidade], excedeu: d.excedeu, comCamada: c.unidades?.comCamada ?? 0, semCamada: [...(c.unidades?.semCamada ?? [])],
          total: c.total, sugestao: c.fonte === 'tratio',
        };
      },
      projetar: (lat: number, lon: number, cota: number) => {
        const m = motor.current;
        if (!m) return null;
        // A câmera e o grupo atualizados na hora: o `project` usa a matriz do mundo, que só o quadro seguinte refaria.
        posicionarCamera(m);
        m.camera.updateMatrixWorld();
        m.grupo.updateMatrixWorld();
        const q = plano.paraPlano(lat, lon);
        const s = naTela(m, q.x, q.y, cota);
        if (!s) return null;
        const r = m.renderer.domElement.getBoundingClientRect();
        return { x: r.left + s.x, y: r.top + s.y };
      },
    };
    Object.defineProperty(el, 'ponteDo3d', { value: ponte, configurable: true });
    return () => { delete (el as unknown as { ponteDo3d?: unknown }).ponteDo3d; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plano, g]);

  // A captura (316.5): um quadro desenhado na hora, no tamanho pedido, composto com o texto.
  const textoDaVista = `exagero ×${numeroBr(exagero, 0)} · opacidade do satélite ${porcento(opacidade)}`;
  const credito = p.imagem?.credito ?? null;
  const aoProntoRef = useRef(p.aoPronto);
  aoProntoRef.current = p.aoPronto;
  const textoRef = useRef({ textoDaVista, credito, legenda });
  textoRef.current = { textoDaVista, credito, legenda };
  useEffect(() => {
    if (estado !== 'pronto') return;
    aoProntoRef.current?.({
      capturar: async ({ largura, altura }) => {
        const m = motor.current;
        const el = caixa.current;
        if (!m || !el) throw new Error('o modelo 3D não está montado');
        const { T } = m;
        const alvo = new T.WebGLRenderTarget(largura, altura, { colorSpace: T.SRGBColorSpace });
        const aspecto = m.camera.aspect;
        try {
          posicionarCamera(m);
          m.camera.aspect = largura / altura;
          m.camera.updateProjectionMatrix();
          m.renderer.setRenderTarget(alvo);
          m.renderer.clear();
          m.renderer.render(m.cena, m.camera);
          const pixels = new Uint8Array(largura * altura * 4);
          m.renderer.readRenderTargetPixels(alvo, 0, 0, largura, altura, pixels);
          // O norte da captura: o mesmo ângulo da tela, no quadro e no aspecto da captura.
          const angulo = anguloDoNorte(m, { largura, altura }) ?? 0;
          const canvas = document.createElement('canvas');
          canvas.width = largura; canvas.height = altura;
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('sem canvas 2D para compor a vista');
          // Sempre o papel CLARO (o relatório é papel), qualquer que seja o tema da tela.
          const papel = coresDoPapelClaro(el);
          ctx.fillStyle = papel.papel;
          ctx.fillRect(0, 0, largura, altura);
          // O render target vem de baixo para cima: vira as linhas.
          const imagem = ctx.createImageData(largura, altura);
          for (let y = 0; y < altura; y++) imagem.data.set(pixels.subarray((altura - 1 - y) * largura * 4, (altura - y) * largura * 4), y * largura * 4);
          const fundo = document.createElement('canvas');
          fundo.width = largura; fundo.height = altura;
          fundo.getContext('2d')?.putImageData(imagem, 0, 0);
          ctx.drawImage(fundo, 0, 0);
          const k = Math.max(1, altura / 675);
          const fonte = getComputedStyle(el).fontFamily;
          const linhas = [LEGENDA_DO_ESPACIAL, textoRef.current.textoDaVista, ...(textoRef.current.credito ? [textoRef.current.credito] : [])];
          ctx.font = `${Math.round(14 * k)}px ${fonte}`;
          ctx.fillStyle = papel.tinta;
          ctx.textBaseline = 'bottom';
          linhas.reverse().forEach((t, i) => ctx.fillText(t, 12 * k, altura - 8 * k - i * 20 * k));
          // A legenda de cores (a mesma da tela), no canto de cima à esquerda: amostra, número e texto.
          const leg = textoRef.current.legenda;
          if (leg.itens.length) {
            ctx.textBaseline = 'middle';
            ctx.font = `bold ${Math.round(14 * k)}px ${fonte}`;
            ctx.fillText(leg.titulo, 12 * k, 20 * k);
            ctx.font = `${Math.round(14 * k)}px ${fonte}`;
            leg.itens.forEach((it, i) => {
              const y = 20 * k + (i + 1) * 20 * k;
              ctx.fillStyle = rgb(it.tipo === 'zona' ? m.cores.zonas[it.numero] : m.cores.escala[it.numero - 1]);
              ctx.fillRect(12 * k, y - 6 * k, 12 * k, 12 * k);
              ctx.strokeStyle = papel.tinta3;
              ctx.lineWidth = Math.max(1, k);
              ctx.strokeRect(12 * k, y - 6 * k, 12 * k, 12 * k);
              ctx.fillStyle = papel.tinta;
              ctx.fillText(it.texto, 30 * k, y);
            });
          }
          // O norte, no canto de cima à direita, girado como na tela: a seta e o «N» na ponta.
          const cx = largura - 32 * k, cy = 36 * k;
          ctx.save();
          ctx.translate(cx, cy);
          ctx.rotate(angulo);
          ctx.strokeStyle = papel.tinta;
          ctx.fillStyle = papel.tinta;
          ctx.lineWidth = Math.max(1, 2 * k);
          ctx.beginPath(); ctx.moveTo(0, 14 * k); ctx.lineTo(0, -10 * k); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(0, -16 * k); ctx.lineTo(-6 * k, -6 * k); ctx.lineTo(6 * k, -6 * k); ctx.closePath(); ctx.fill();
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          ctx.font = `bold ${Math.round(14 * k)}px ${fonte}`;
          ctx.fillText('N', 0, -18 * k);
          ctx.restore();
          return await new Promise<Blob>((ok, falha) => canvas.toBlob((b) => (b ? ok(b) : falha(new Error('a vista não virou imagem'))), 'image/png'));
        } finally {
          m.renderer.setRenderTarget(null);
          alvo.dispose();
          m.camera.aspect = aspecto;
          m.camera.updateProjectionMatrix();
          desenhar();
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado]);

  /* ── A tela ───────────────────────────────────────────────────────── */

  // As fichas dos voxels são os itens da legenda: as zonas presentes (SBTn) ou as classes com os limites (Su e contínuas).
  const zonasPossiveis = legenda.itens;
  const quadradoDaCor = (it: ItemDaLegenda | null): JSX.Element => (
    <svg className="cptu-amostra-da-zona" width="14" height="14" aria-hidden="true">
      <rect width="14" height="14" fill={it ? rgb(it.tipo === 'zona' ? cores?.zonas[it.numero] : cores?.escala[it.numero - 1]) : 'var(--ts-tinta-3)'} />
    </svg>
  );
  const semDesenho = estado === 'sem-webgl' || estado === 'erro';
  // Sem desenho 3D, a fatia em planta em 2D (a mesma cota da fatia em planta do 3D).
  const plantaEm2d = useMemo(() => (semDesenho && g && !insuficiente ? fatiaEmPlanta(g, p.verticais, fatias.planta.posicao) : null),
    [semDesenho, g, insuficiente, p.verticais, fatias.planta.posicao]);
  // A legenda das fitas: as zonas presentes, ou a grandeza com a escala escrita (a cor nunca vai sozinha).
  const casasDe = (a: number, b: number): number => (Math.abs(b - a) >= 10 ? 0 : Math.abs(b - a) >= 1 ? 1 : 2);
  const legendaDasFitas = (() => {
    if (!sobre || !fitas.length) return null;
    const d = sobre.dados.grandeza;
    if (d.tipo === 'zona') {
      return <LegendaSbt tipo="sbtn" presentes={new Set(sobre.dados.zonasPresentes)} soPresentes titulo={`Fitas das verticais: zonas ${d.rotulo}, medidas, ${NO_LADO[aj.ladoDaFita]} de cada vertical`} />;
    }
    const u = d.unidade ? ` ${d.unidade}` : '';
    const escalaDe = (f: FitaDaVertical): string => `${numeroBr(f.dominio[0], casasDe(f.dominio[0], f.dominio[1]))} a ${numeroBr(f.dominio[1], casasDe(f.dominio[0], f.dominio[1]))}${u}`
      + (sobre.dados.tipo === 'log' ? ' (eixo log)' : '');
    const comum = sobre.dados.escala === 'comum' || !!d.dominio || !!sobre.dados.fixos;
    return (
      <p className="cptu-nota cptu-3d-fitas-legenda">
        Fitas das verticais: {d.rotulo}{u ? ` (${d.unidade})` : ''} medido, {NO_LADO[aj.ladoDaFita]} de cada vertical — da face da coluna (o início da
        escala) à largura toda de {numeroBr(fitas[0].largura, 1)} m (o fim; exagero horizontal ×{numeroBr(aj.exageroHorizontal, Number.isInteger(aj.exageroHorizontal) ? 0 : 1)}),
        com o traço do perfil;{' '}
        {comum ? `a mesma escala em todas: ${escalaDe(fitas[0])}.` : `a escala de cada vertical: ${fitas.map((f) => `${f.codigo} ${escalaDe(f)}`).join('; ')}.`}
      </p>
    );
  })();

  const parametros = g ? `grade ${numeroBr(g.grade.passo.x, 0)} × ${numeroBr(g.grade.passo.y, 0)} × ${numeroBr(g.grade.passo.z, 2)} m · ${textoDaVista}` : '';
  const vg = g?.variogramas.find((v) => v.coluna === principal) ?? null;
  const metodo = !g ? '' : g.metodoUsado === 'krigagem' && vg
    ? `krigagem (${vg.ajustado.modelo === 'esferico' ? 'esférico' : 'exponencial'}): pepita ${numeroBr(vg.ajustado.pepita, 3)} · patamar ${numeroBr(vg.ajustado.patamar, 3)} · alcance ${numeroBr(vg.ajustado.alcance, 2)} m${vg.ajustado.motivo ? ` — ${vg.ajustado.motivo}` : ''}`
    : `IDW (potência 2)${g.motivoDoMetodo ? ` — ${g.motivoDoMetodo}` : ''}`;
  const extremos = (tipo: TipoDeFatia): { de: number; ate: number; passo: number } => {
    if (!g) return { de: 0, ate: 0, passo: 1 };
    const { origem, passo, n } = g.grade;
    if (tipo === 'ns') return { de: origem.x, ate: origem.x + (n.x - 1) * passo.x, passo: passo.x };
    if (tipo === 'eo') return { de: origem.y, ate: origem.y + (n.y - 1) * passo.y, passo: passo.y };
    return { de: origem.cota, ate: origem.cota + (n.z - 1) * passo.z, passo: passo.z };
  };
  const rotuloDaPosicao = (tipo: TipoDeFatia, v: number): string => (tipo === 'ns' ? `${numeroBr(v, 1)} m E`
    : tipo === 'eo' ? `${numeroBr(v, 1)} m N` : `cota ${numeroBr(v, 2)} m`);

  return (
    <section ref={caixa} className={`cptu-3d${p.celular ? ' celular' : ''}`} aria-label="Modelo 3D da campanha">
      <p className="cptu-legenda cptu-3d-legenda">{LEGENDA_DO_ESPACIAL}</p>
      {!g ? <p className="cptu-nota" role="status">Carregando o modelo 3D…</p> : null}
      {insuficiente ? <p className="cptu-aviso" role="status">{insuficiente}</p> : null}
      {g && !insuficiente ? (
        <>
          {estado === 'sem-webgl' ? (
            <p className="cptu-aviso" role="status">Este navegador não desenha 3D (sem WebGL) — a seção e a fatia em planta continuam</p>
          ) : null}
          {estado === 'erro' ? <p className="cptu-aviso" role="status">{erro}</p> : null}
          {semDesenho ? null : (
            <>
            <div className="cptu-3d-controles">
              <fieldset className="cptu-3d-grupo cptu-3d-verticais">
                <legend>Verticais</legend>
                <div className="cptu-3d-fichas" role="group" aria-label="Forma">
                  {(['cilindro', 'prisma'] as const).map((f) => (
                    <button key={f} type="button" className="cptu-3d-ficha" aria-pressed={aj.forma === f} onClick={() => mudar({ forma: f })}>
                      {f === 'cilindro' ? 'Cilindro' : 'Prisma'}
                    </button>
                  ))}
                </div>
                <label className="cptu-3d-faixa">
                  <span>Raio das verticais {numeroBr(aj.raio, 2)} m</span>
                  <input type="range" min={RAIO_DAS_VERTICAIS.minimo} max={RAIO_DAS_VERTICAIS.maximo} step={RAIO_DAS_VERTICAIS.passo} value={aj.raio}
                    aria-label="Raio das verticais" onChange={(ev) => mudar({ raio: Math.round(Number(ev.target.value) * 10) / 10 })} />
                </label>
                <label className="cptu-3d-escolha">
                  <span>Colorir as verticais por</span>
                  <select value={corDasColunas} onChange={(ev) => mudar({ corDasVerticais: ev.target.value as AjustesDo3d['corDasVerticais'] })}>
                    {CORES_DAS_VERTICAIS.map((c) => (
                      <option key={c.chave} value={c.chave} disabled={!!bloqueioDaCor(c.chave)}>{c.rotulo}</option>
                    ))}
                  </select>
                </label>
                {CORES_DAS_VERTICAIS.filter((c) => bloqueioDaCor(c.chave)).map((c) => (
                  <p key={c.chave} className="cptu-nota cptu-3d-motivo">{c.rotulo}: {bloqueioDaCor(c.chave)}</p>
                ))}
                <label className="cptu-3d-faixa">
                  <span>Opacidade das verticais {porcento(aj.opacidadeDasVerticais)}</span>
                  <input type="range" min={OPACIDADE_MINIMA_DAS_VERTICAIS * 100} max={100} step={5} value={Math.round(aj.opacidadeDasVerticais * 100)}
                    aria-label="Opacidade das verticais" onChange={(ev) => mudar({ opacidadeDasVerticais: Number(ev.target.value) / 100 })} />
                </label>
              </fieldset>
              <fieldset className="cptu-3d-grupo">
                <legend>Gráficos sobre as verticais</legend>
                <label className="cptu-3d-faixa">
                  <span>Exagero horizontal dos gráficos ×{numeroBr(aj.exageroHorizontal, Number.isInteger(aj.exageroHorizontal) ? 0 : 1)}</span>
                  <input type="range" min={EXAGERO_HORIZONTAL.minimo} max={EXAGERO_HORIZONTAL.maximo} step={EXAGERO_HORIZONTAL.passo}
                    value={aj.exageroHorizontal} disabled={!fitas.length} aria-label="Exagero horizontal dos gráficos"
                    onChange={(ev) => mudar({ exageroHorizontal: Math.round(Number(ev.target.value) * 2) / 2 })} />
                </label>
                <label className="cptu-3d-escolha">
                  <span>Lado da fita</span>
                  <select value={aj.ladoDaFita} disabled={!fitas.length}
                    onChange={(ev) => mudar({ ladoDaFita: ev.target.value as AjustesDo3d['ladoDaFita'] })}>
                    {LADOS_DA_FITA.map((l) => <option key={l.chave} value={l.chave}>{l.rotulo}</option>)}
                  </select>
                </label>
                {fitas.length ? null : <p className="cptu-nota cptu-3d-motivo">Escolha um gráfico para as verticais para ajustar as fitas</p>}
              </fieldset>
              <fieldset className="cptu-3d-grupo cptu-3d-camadas">
                <legend>Camadas geotécnicas</legend>
                <label className="cptu-3d-escolha">
                  <span>Camadas usadas</span>
                  <select value={fonte} onChange={(ev) => mudar({ camadas: ev.target.value as FonteDasCamadas })}>
                    {/* A sugestão só aparece onde a tela a tem (o Portal não: RN-255); as gravadas, sempre (bloqueadas sem camada). */}
                    {FONTES_DAS_CAMADAS.filter((f) => f.chave !== 'tratio' || temSugestao).map((f) => (
                      <option key={f.chave} value={f.chave} disabled={!fonteServe(f.chave)}>{f.rotulo}</option>
                    ))}
                  </select>
                </label>
                {temGravadas ? null : <p className="cptu-nota cptu-3d-motivo">As gravadas pelo engenheiro: nenhuma vertical tem camada gravada</p>}
                {fonte !== 'nenhuma' && unidades ? (
                  <>
                    {sugestao ? (
                      <p className="cptu-aviso cptu-3d-sugestao">
                        {FAIXA_DA_SUGESTAO}: o T-ratio calculado neste aparelho, com a unidade pela zona SBTn predominante; não vai ao relatório (RN-269)
                      </p>
                    ) : null}
                    <div className="cptu-3d-fichas" role="group" aria-label="Desenho das camadas">
                      {(['blocos', 'contatos'] as const).map((d) => (
                        <button key={d} type="button" className="cptu-3d-ficha" aria-pressed={aj.desenhoDasCamadas === d} onClick={() => mudar({ desenhoDasCamadas: d })}>
                          {d === 'blocos' ? 'Blocos' : 'Só contatos'}
                        </button>
                      ))}
                    </div>
                    <label className="cptu-3d-faixa">
                      <span>Opacidade das camadas {porcento(aj.opacidadeDasCamadas)}</span>
                      <input type="range" min={0} max={100} step={5} value={Math.round(aj.opacidadeDasCamadas * 100)}
                        aria-label="Opacidade das camadas" onChange={(ev) => mudar({ opacidadeDasCamadas: Number(ev.target.value) / 100 })} />
                    </label>
                    <p className="cptu-nota">
                      {FONTES_DAS_CAMADAS.find((f) => f.chave === fonte)?.rotulo}: camadas em {numeroBr(unidades.comCamada, 0)} de{' '}
                      {numeroBr(g.verticais.length, 0)} verticais{unidades.semCamada.length ? ` (sem camada: ${unidades.semCamada.join(', ')})` : ''};
                      entre elas, estimado
                    </p>
                    {unidades.unidades.length ? (
                      <ul className="cptu-3d-unidades" aria-label={sugestao ? `Unidades (${FAIXA_DA_SUGESTAO})` : 'Unidades'}>
                        {unidades.unidades.map((u, i) => {
                          const chave = normalizarUnidade(u);
                          const escondida = aj.unidadesEscondidas.some((x) => normalizarUnidade(x) === chave);
                          const z = /^--ts-sbtn-(\d)$/.exec(tokensDasUnidades[i] ?? '');
                          return (
                            <li key={chave}>
                              <label className="cptu-3d-caixa">
                                <input type="checkbox" checked={!escondida} onChange={() => mudar({
                                  unidadesEscondidas: escondida ? aj.unidadesEscondidas.filter((x) => normalizarUnidade(x) !== chave) : [...aj.unidadesEscondidas, chave],
                                })} />
                                {quadradoDaCor(z ? { tipo: 'zona', numero: Number(z[1]), texto: u } : null)}
                                {u}
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    ) : <p className="cptu-nota cptu-3d-motivo">Nenhuma camada com unidade nas verticais do modelo</p>}
                    {camadasExcedem ? (
                      <p className="cptu-aviso">As camadas passam do limite de {numeroBr(VOXELS_NO_MAXIMO, 0)} voxels: aumente o passo da grade</p>
                    ) : null}
                  </>
                ) : null}
              </fieldset>
              <fieldset className="cptu-3d-grupo">
                <legend>Voxels</legend>
                <div className="cptu-3d-fichas">
                  {zonasPossiveis.map((it) => {
                    const z = it.numero;
                    const ligada = zonasDosVoxels.has(z);
                    return (
                      <button key={`${it.tipo}-${z}`} type="button" className="cptu-3d-ficha" aria-pressed={ligada}
                        onClick={() => setZonasDosVoxels((s) => { const n = new Set(s); if (n.has(z)) n.delete(z); else n.add(z); return n; })}>
                        {quadradoDaCor(it)}
                        <span>{it.texto}</span>
                      </button>
                    );
                  })}
                </div>
                {voxels?.excedeu && voxels.passoSugerido ? (
                  <p className="cptu-aviso">
                    {numeroBr(voxels.n, 0)} voxels passam do limite de {numeroBr(VOXELS_NO_MAXIMO, 0)}: use o passo{' '}
                    {numeroBr(voxels.passoSugerido.x, 0)} × {numeroBr(voxels.passoSugerido.y, 0)} × {numeroBr(voxels.passoSugerido.z, 2)} m
                  </p>
                ) : null}
              </fieldset>
              {(['ns', 'eo', 'planta'] as const).map((tipo) => {
                const f = fatias[tipo];
                const e = extremos(tipo);
                return (
                  <fieldset key={tipo} className="cptu-3d-grupo">
                    <label className="cptu-3d-caixa">
                      <input type="checkbox" checked={f.ligada}
                        onChange={(ev) => setFatias((x) => ({ ...x, [tipo]: { ...x[tipo], ligada: ev.target.checked } }))} />
                      {ROTULO_DA_FATIA[tipo]}
                    </label>
                    <label className="cptu-3d-faixa">
                      <span>{rotuloDaPosicao(tipo, f.posicao)}</span>
                      <input type="range" min={e.de} max={e.ate} step={e.passo} value={f.posicao} disabled={!f.ligada}
                        aria-label={`${ROTULO_DA_FATIA[tipo]}: posição`}
                        onChange={(ev) => setFatias((x) => ({ ...x, [tipo]: { ...x[tipo], posicao: Number(ev.target.value) } }))} />
                    </label>
                  </fieldset>
                );
              })}
              <fieldset className="cptu-3d-grupo">
                <label className="cptu-3d-caixa">
                  <input type="checkbox" checked={comCerca} onChange={(ev) => setComCerca(ev.target.checked)} />
                  Cerca {p.linhas.length ? `(${p.linhas.map((l) => l.nome).join(', ')})` : '(nenhuma linha)'}
                </label>
                <label className="cptu-3d-caixa">
                  <input type="checkbox" checked={comSatelite} onChange={(ev) => setComSatelite(ev.target.checked)} />
                  Satélite sobre o terreno
                </label>
                <label className="cptu-3d-faixa">
                  <span>opacidade do satélite {porcento(opacidade)}</span>
                  <input type="range" min={0} max={100} step={5} value={Math.round(opacidade * 100)} disabled={!comSatelite || !p.imagem}
                    aria-label="Opacidade do satélite" onChange={(ev) => setOpacidade(Number(ev.target.value) / 100)} />
                </label>
                <label className="cptu-3d-caixa">
                  <input type="checkbox" checked={aj.malha} onChange={(ev) => mudar({ malha: ev.target.checked })} />
                  Malha de cota e coordenadas
                </label>
                {sabeDoNa ? (
                  <>
                    <label className="cptu-3d-caixa">
                      <input type="checkbox" checked={aj.superficieDoNa} onChange={(ev) => mudar({ superficieDoNa: ev.target.checked })} />
                      Superfície do nível d'água
                    </label>
                    <label className="cptu-3d-faixa">
                      <span>Opacidade da água {porcento(aj.opacidadeDaAgua)}</span>
                      <input type="range" min={0} max={100} step={5} value={Math.round(aj.opacidadeDaAgua * 100)}
                        disabled={!aj.superficieDoNa || !aguaTemCelula}
                        aria-label="Opacidade da água" onChange={(ev) => mudar({ opacidadeDaAgua: Number(ev.target.value) / 100 })} />
                    </label>
                    {agua && 'motivo' in agua ? <p className="cptu-nota cptu-3d-motivo">Nível d'água: {agua.motivo}</p> : null}
                    {agua && 'cotas' in agua && !aguaTemCelula && aj.superficieDoNa ? (
                      <p className="cptu-nota cptu-3d-motivo">Nível d'água: nenhuma célula da grade cabe no fecho das verticais (só os anéis): diminua o passo</p>
                    ) : null}
                    {agua && 'cotas' in agua && aguaTemCelula && aj.superficieDoNa ? (
                      <p className="cptu-nota">
                        Nível d'água em {numeroBr(agua.usadas.length, 0)} verticais (os anéis, no NA de cada vertical); a superfície é estimada entre as verticais
                        {agua.motivoDoMetodo ? ` — ${agua.motivoDoMetodo}` : ''}
                      </p>
                    ) : null}
                  </>
                ) : null}
                <label className="cptu-3d-faixa">
                  <span>Exagero vertical ×{numeroBr(exagero, 0)}</span>
                  <input type="range" min={1} max={10} step={1} value={exagero} aria-label="Exagero vertical"
                    onChange={(ev) => setExagero(Number(ev.target.value))} />
                </label>
              </fieldset>
            </div>
            <div className="cptu-3d-giro" role="toolbar" aria-label="Girar o modelo">
              <button type="button" className="cptu-3d-botao" aria-label="Girar para a esquerda" onClick={() => girar(-GIRO, 0)}>←</button>
              <button type="button" className="cptu-3d-botao" aria-label="Girar para a direita" onClick={() => girar(GIRO, 0)}>→</button>
              <button type="button" className="cptu-3d-botao" aria-label="Inclinar para cima" onClick={() => girar(0, GIRO)}>↑</button>
              <button type="button" className="cptu-3d-botao" aria-label="Inclinar para baixo" onClick={() => girar(0, -GIRO)}>↓</button>
              <button type="button" className="cptu-3d-botao" aria-label="Aproximar" onClick={() => girar(0, 0, 1 / ZOOM)}>+</button>
              <button type="button" className="cptu-3d-botao" aria-label="Afastar" onClick={() => girar(0, 0, ZOOM)}>−</button>
              <button type="button" className="botao secundario" onClick={voltar}>Vista de partida</button>
            </div>
            {estado === 'carregando' ? <p className="cptu-nota" role="status">Carregando o modelo 3D…</p> : null}
            {sugestao ? <p className="cptu-aviso cptu-3d-faixa-da-sugestao" role="status">{FAIXA_DA_SUGESTAO} — as camadas do 3D são a sugestão do T-ratio (RN-269)</p> : null}
            </>
          )}
          <div ref={tela} className="cptu-3d-tela" tabIndex={0} role="application" hidden={semDesenho}
            aria-label="Modelo 3D: as setas giram e inclinam, + e − aproximam, Home volta à vista de partida; clique numa coluna ou numa fatia para ler"
            onKeyDown={aoTeclar} onPointerDown={aoApertar} onPointerMove={aoMover} onPointerUp={aoSoltar}
            onPointerCancel={() => { arrasto.current = null; }}>
            <div ref={rotulos} className="cptu-3d-rotulos" aria-hidden="true">
              {p.verticais.map((v) => <span key={v.codigo} className="cptu-3d-rotulo">{v.codigo}</span>)}
            </div>
            <div ref={rotulosDaMalha} className="cptu-3d-rotulos" aria-hidden="true">
              {malha && aj.malha ? malha.rotulos.map((r, i) => (
                <span key={i} className={`cptu-3d-malha-rotulo cptu-3d-malha-${r.eixo}`} data-paredes={r.paredes.join('')} hidden>{r.texto}</span>
              )) : null}
            </div>
            <span ref={norte} className="cptu-3d-norte" aria-label="norte">N ↑</span>
          </div>
          {plantaEm2d ? (
            <section className="cptu-3d-planta" aria-label="Fatia em planta em 2D">
              <label className="cptu-3d-faixa">
                <span>Fatia em planta: {rotuloDaPosicao('planta', plantaEm2d.cota)}</span>
                <input type="range" min={extremos('planta').de} max={extremos('planta').ate} step={extremos('planta').passo} value={fatias.planta.posicao}
                  aria-label="Fatia em planta: cota"
                  onChange={(ev) => setFatias((x) => ({ ...x, planta: { ...x.planta, posicao: Number(ev.target.value) } }))} />
              </label>
              <SecaoEspacial planta={plantaEm2d} grandeza={p.grandeza} mostrarNaoHomologadas={p.mostrarNaoHomologadas} />
            </section>
          ) : null}
          {semDesenho ? null : (
            <>
              {legendaDasFitas}
              {aj.superficieDoNa && agua && 'cotas' in agua && aguaTemCelula ? (
                <p className="cptu-nota cptu-3d-agua-legenda">
                  Nível d'água: os anéis no NA de cada vertical; a superfície é estimada entre as verticais
                </p>
              ) : null}
              {legenda.itens.length ? (
                <figure className="cptu-legenda cptu-3d-legenda-cores" aria-label={legenda.titulo}>
                  <figcaption className="cptu-legenda-titulo">{legenda.titulo}</figcaption>
                  <ul>
                    {legenda.itens.map((it) => (
                      <li key={`${it.tipo}-${it.numero}`}>{quadradoDaCor(it)}<span>{it.texto}</span></li>
                    ))}
                  </ul>
                </figure>
              ) : null}
              <p className="cptu-leitura cptu-3d-leitura" aria-live="polite">{leitura ?? 'Clique numa coluna (medido) ou numa fatia (estimado) para ler a cota.'}</p>
            </>
          )}
          <p className="cptu-nota">Método: {metodo}</p>
          <p className="cptu-nota">
            Parâmetros: {parametros} · <span className="cptu-3d-escolhas">ⓘ escolhas da tela, sem fonte — modelo interpretativo</span>
          </p>
          {p.imagem ? <p className="cptu-nota cptu-3d-credito">{p.imagem.credito}</p> : (
            <p className="cptu-nota">
              sem a imagem de satélite: {p.motivoSemImagem ?? 'a imagem não veio'}{' '}
              {p.aoRecarregarImagem ? <button type="button" className="botao discreto" onClick={p.aoRecarregarImagem}>Recarregar a imagem</button> : null}
            </p>
          )}
        </>
      ) : null}
    </section>
  );
}
